import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingDrawerCodeChip, BookingDrawerHeaderIdentity, DrawerGuestFieldLink } from './DrawerEntityLink';
import {
  Drawer,
  Box,
  Typography,
  Divider,
  IconButton,
  Chip,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Autocomplete,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Collapse,
  Card,
  CardContent,
  Checkbox,
  Avatar,
  Snackbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  CalendarToday as CalendarIcon,
  Hotel as HotelIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Schedule as ScheduleIcon,
  Image as ImageIcon,
  NightsStay as NightsStayIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  PersonAdd as PersonAddIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  CameraAlt as CameraAltIcon,
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  Room as RoomIcon,
  ChildCare as ChildCareIcon,
  HelpOutline as HelpOutlineIcon,
  CheckCircle as CheckCircleIcon,
  FactCheck as FactCheckIcon,
  SwapHoriz as RoomChangeIcon,
  TrendingUp as RoomUpgradeIcon,
  TrendingDown as RoomDowngradeIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { formatPaymentTime, toPaymentProcessedAtIso } from '../../utils/dateUtils';
import {
  resolveStayCheckInDisplay,
  resolveStayCheckOutDisplay,
  resolveActualCheckInDisplay,
  resolveActualCheckOutDisplay,
  isSameDayStay,
  resolveRoomSegmentStayMeta,
} from '../../utils/bookingStayDisplay';
import {
  buildRoomTypeSortOrderMap,
  resolveRoomTypeChangeKindFromItems,
  getRoomTypeChangeLabel,
  isGuestChild,
} from '../../utils/bookingUtils';
import ActualStayDateChips from './ActualStayDateChips';
import StayDurationChip from './StayDurationChip';
import { HotelDatePicker, HotelDateTimePicker as DateTimePicker } from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  getBookingDetails,
  getBookingGuestDocuments,
  updateBookingStatus,
  markBookingNoShow,
  searchGuestByPhone,
  getCustomerDetail,
} from '../../services/api';
import api from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import countriesData from '../../data/countries-states-cities-data';
import countryCodesData from '../../data/country-codes.json';
import ChannelImage from '../common/ChannelImage';
import PaymentLinkDrawerOverlay from './PaymentLinkDrawerOverlay';
import PaymentDrawerHeaderTitle from './PaymentDrawerHeaderTitle';
import BookingAuditDrawer from './BookingAuditDrawer';
import CheckInDrawer from './CheckInDrawer';
import WalkInAiCheckinOverlay from '../WalkInAiCheckinOverlay';
import ForeignGuestDetailsDrawer from './ForeignGuestDetailsDrawer';
import {
  EMPTY_FOREIGN_GUEST_DETAILS,
  applyForeignCountryChange,
  foreignDetailsToGuestApiPayload,
  getForeignGuestChipSx,
  isForeignGuestCountry,
  isForeignGuestRecord,
  mapGuestApiToForeignDetails,
  validateForeignGuestDetails,
} from '../../utils/foreignGuestFormUtils';
import {
  filterPaymentMethodsForComm,
  PAYMENT_LINK_METHOD,
  useBookingPaymentLinkComm,
} from '../../hooks/useBookingPaymentLinkComm';
import { PAYMENT_METHOD_CHIPS } from '../../utils/paymentMethodUtils';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../../utils/locationSearch';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  getGuestDrawerContentSx,
  getGuestDrawerUploadPaperSx,
  getGuestDrawerMetaChipSx,
  bookingDrawerRoomImageSizeSx,
  BOOKING_DRAWER_ACCENT,
  createBookingDrawerOverlayProps,
  withDrawerMenuPaperSx,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import { GuestDocumentsDrawer, buildGuestDocumentsPayload } from './guestDocuments';
import {
  formatAadhaarDisplay,
  isAadhaarDocumentType,
  isValidPassportIdNumber,
  normalizeDocumentIdForSubmit,
  normalizeDocumentIdInput,
  validateAadhaarIdNumber,
  validateGuestDocumentImages,
} from '../../utils/documentIdFormatters';
import {
  formatPhoneLengthLabel,
  getPhoneMaxLength,
  normalizePhoneInputForCountry,
  validatePhoneForCountry,
} from '../../utils/phoneValidation';
import { captureNativeCameraDataUrl, getNativeCameraErrorMessage } from '../../utils/nativeCameraCapture';
import { notifyBookingMutation } from '../../utils/bookingSync';
import {
  getBookingCurrencySymbol,
  getPaymentSummaryData,
  getEnrichedPaymentSummaryData,
  isBookingPaymentClear,
} from '../../utils/bookingPaymentSummary';
import BookingPaymentSummaryCard from './BookingPaymentSummaryCard';
import BookingDrawerPaymentCard from './BookingDrawerPaymentCard';
import BookingFinancialHistoryPanel from './BookingFinancialHistoryPanel';
import { AiGradientButton } from '../../shared';
import { getPermissionDeniedMessage, PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

const CUSTOM_BORDER_COLOR = '#4ce1b6';
const NO_DOCUMENT_VALUE = 'no_document';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const toGuestDocUrl = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  if (s.startsWith('http') || s.startsWith('data:')) return s;
  return s.startsWith('/')
    ? `${window.location.origin}${s}`
    : `${window.location.origin}/${s}`;
};

const resolveHotelCountryName = () => {
  const hotel = (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel) || null;
  if (!hotel?.country) return '';
  const hotelCountryValue = String(hotel.country).trim();
  let countryObj = countriesData.countries.find(
    (c) => c.code === hotelCountryValue || c.name === hotelCountryValue,
  );
  if (!countryObj) {
    countryObj = countriesData.countries.find(
      (c) =>
        c.code?.toLowerCase() === hotelCountryValue.toLowerCase()
        || c.name?.toLowerCase() === hotelCountryValue.toLowerCase(),
    );
  }
  return countryObj?.name || hotelCountryValue;
};

const BookingDetailsDrawer = ({ open, onClose, bookingId, showNoShowButton = false, onStatusUpdate }) => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const canViewBookingAudit = can(PERMISSIONS.BOOKING_AUDIT_VIEW);
  const canManageAiCheckin = can(PERMISSIONS.AI_CHECKIN_MANAGE);
  const canEditBooking = can(PERMISSIONS.BOOKING_MODIFY);
  const normalizeBookingId = useCallback((value) => {
    const parsedId = Number(value);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  }, []);
  const [booking, setBooking] = useState(null);
  // Removed loading state - drawer opens immediately, content shows as data arrives
  const [error, setError] = useState(null);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [checkInDrawerOpen, setCheckInDrawerOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteGuestDialogOpen, setDeleteGuestDialogOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);
  const [guestMenuAnchor, setGuestMenuAnchor] = useState(null);
  const [selectedGuestForAction, setSelectedGuestForAction] = useState(null);
  const [expandedGuestId, setExpandedGuestId] = useState(null);
  const [guestSlideIndex, setGuestSlideIndex] = useState({}); // {roomId: slideIndex} for guest slider per room
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);
  const [selectedGuestDocuments, setSelectedGuestDocuments] = useState(null);
  const [guestFormDrawerOpen, setGuestFormDrawerOpen] = useState(false);
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(() => moment());
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentLinkSendOpen, setPaymentLinkSendOpen] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [paymentCameraOpen, setPaymentCameraOpen] = useState(false);
  const [paymentStream, setPaymentStream] = useState(null);
  const [paymentCameraUpload, setPaymentCameraUpload] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [roomTypeImages, setRoomTypeImages] = useState({}); // Map of room_type_id -> image_url (extracted from booking data)
  const {
    canSendPaymentLink,
    canSendPaymentLinkEmail,
    canSendPaymentLinkWhatsApp,
  } = useBookingPaymentLinkComm();
  const paymentMethods = useMemo(
    () => filterPaymentMethodsForComm(PAYMENT_METHOD_CHIPS, canSendPaymentLink),
    [canSendPaymentLink]
  );
  const paymentReceiptVideoRef = useRef(null);
  const paymentReceiptCanvasRef = useRef(null);
  const paymentReceiptFileInputRef = useRef(null);
  const paymentDrawerRootRef = useRef(null);
  const bookingModifiedRef = useRef(false);
  const [editingGuestItemId, setEditingGuestItemId] = useState(null);
  const [editingGuestId, setEditingGuestId] = useState(null);
  const [stayGuestReplaceId, setStayGuestReplaceId] = useState(null);
  const [isStayGuestEdit, setIsStayGuestEdit] = useState(false);
  const [isPhoneLocked, setIsPhoneLocked] = useState(false);
  const [savingGuest, setSavingGuest] = useState(false);
  const [guestFormError, setGuestFormError] = useState(null);
  const [individualGuestFormData, setIndividualGuestFormData] = useState({
    title: '',
    name: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    phone_country_code: '+91',
    dob: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
    camera_upload: false,
    do_not_rent: false,
    do_not_rent_severity: 'HIGH',
    do_not_rent_reason: '',
    id_front: '',
    id_back: '',
    document_type: NO_DOCUMENT_VALUE,
    id_number: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [aiAddGuestOpen, setAiAddGuestOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [titleMenuOpen, setTitleMenuOpen] = useState(false);
  const [guestSearchOptions, setGuestSearchOptions] = useState([]);
  const [phoneSearchDebounceTimer, setPhoneSearchDebounceTimer] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraType, setCameraType] = useState(null);
  const [stream, setStream] = useState(null);
  const [cameraPermissionSnackbar, setCameraPermissionSnackbar] = useState({ open: false, message: '' });
  const [paymentLinkToast, setPaymentLinkToast] = useState({ open: false, message: '' });
  const [foreignGuestDetails, setForeignGuestDetails] = useState(EMPTY_FOREIGN_GUEST_DETAILS);
  const [foreignGuestDrawerOpen, setForeignGuestDrawerOpen] = useState(false);
  const phoneCountryCodeAnchorRef = useRef(null);
  const titleMenuAnchorRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputFrontRef = useRef(null);
  const fileInputBackRef = useRef(null);
  
  // Initialize theme synchronously to prevent flash
  const theme = useTheme();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const { selectMenuProps, menuProps, autocompleteSlotProps, datePickerSlotProps } = useMemo(
    () => createBookingDrawerOverlayProps(isDark),
    [isDark],
  );

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    if (!canSendPaymentLink && paymentMethod === PAYMENT_LINK_METHOD) {
      setPaymentMethod('');
      setPaymentLinkSendOpen(false);
    }
  }, [canSendPaymentLink, paymentMethod]);

  const getSafeBookingId = useCallback(() => {
    return (
      normalizeBookingId(bookingId) ||
      normalizeBookingId(booking?.id) ||
      normalizeBookingId(booking?.booking_id)
    );
  }, [bookingId, booking?.id, booking?.booking_id, normalizeBookingId]);

  const markBookingModified = useCallback(() => {
    bookingModifiedRef.current = true;
  }, []);

  const handleDrawerClose = useCallback(() => {
    setCheckInDrawerOpen(false);
    if (bookingModifiedRef.current) {
      onStatusUpdate?.(getSafeBookingId());
      notifyBookingMutation({
        type: 'booking_details_updated',
        bookingId: getSafeBookingId(),
      });
      bookingModifiedRef.current = false;
    }
    onClose?.();
  }, [onClose, onStatusUpdate, getSafeBookingId]);

  useEffect(() => {
    const safeBookingId = normalizeBookingId(bookingId);
    if (open && safeBookingId) {
      bookingModifiedRef.current = false;
      setImageLoaded(false); // Reset image loaded state when drawer opens
      fetchBookingDetails(safeBookingId);
      setGuestSlideIndex({}); // Reset slider when booking changes
      setAuditDrawerOpen(false);
      setCheckInDrawerOpen(false);
    } else if (open && bookingId) {
      bookingModifiedRef.current = false;
      setBooking(null);
      setError('Invalid booking reference. Please refresh and try again.');
      setGuestSlideIndex({});
      setImageLoaded(false);
      setRoomTypeImages({});
      setAuditDrawerOpen(false);
      setCheckInDrawerOpen(false);
    } else {
      setBooking(null);
      setError(null);
      setGuestSlideIndex({});
      setImageLoaded(false);
      setRoomTypeImages({}); // Reset room type images
      setAuditDrawerOpen(false);
      setCheckInDrawerOpen(false);
    }
  }, [open, bookingId]);

  // Auto-fill payment amount with remaining balance when payment drawer opens (2 decimal places)
  useEffect(() => {
    if (paymentDrawerOpen && booking) {
      const summary = getPaymentSummaryData(booking, paymentHistory);
      const remainingBalance = round2(summary.remainingAmount || 0);
      if (remainingBalance > 0) {
        setPaymentAmount(remainingBalance);
      } else {
        setPaymentAmount(0);
      }
    }
  }, [paymentDrawerOpen, booking, paymentHistory]);

  const fetchBookingDetails = async (targetBookingId = null) => {
    const safeBookingId = targetBookingId || getSafeBookingId();
    if (!safeBookingId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    setError(null);
    setImageLoaded(false); // Reset image loaded state when fetching new booking
    setRoomTypeImages({}); // Reset room type images
    try {
      const data = await getBookingDetails(safeBookingId);
      setBooking(data);
      
      // Extract room type images from booking items (images are now included in booking details)
      if (data.items && Array.isArray(data.items)) {
        const imagesMap = {};
        data.items.forEach(item => {
          if (item.room_type && item.room_type.id && item.room_type.image_url) {
            imagesMap[item.room_type.id] = item.room_type.image_url;
          }
        });
        setRoomTypeImages(imagesMap);
      }
    } catch (err) {
      setError(err.message || 'Failed to load booking details');
    }
  };

  const navigateToEditBooking = () => {
    if (!canEditBooking) return;
    const safeBookingId = getSafeBookingId();
    if (!safeBookingId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    handleDrawerClose();
    navigate(`/booking/edit/${safeBookingId}/`);
  };

  const fetchPaymentHistory = async () => {
    if (!bookingId || !booking) return;
    setLoadingPayments(true);
    try {
      // Use booking_code instead of booking ID, as the filter expects booking_code
      const response = await api.get('/finance/api/payments/', {
        params: { booking_code: booking.code || booking.booking_code }
      });
      // Ensure we always set an array
      const data = response.data;
      let payments = [];
      if (data) {
        if (Array.isArray(data)) {
          payments = data;
        } else if (data.Data && Array.isArray(data.Data)) {
          // Handle PaginatedListMixin response format with capital "Data"
          payments = data.Data;
        } else if (data.data && Array.isArray(data.data)) {
          // Handle lowercase "data"
          payments = data.data;
        } else if (data.results && Array.isArray(data.results)) {
          payments = data.results;
        } else if (data.payments && Array.isArray(data.payments)) {
          payments = data.payments;
        }
      }setPaymentHistory(payments);
    } catch (err) {setPaymentHistory([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const notifyCameraPermission = useCallback((message) => {
    setCameraPermissionSnackbar({
      open: true,
      message:
        message ||
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings, or turn off Camera upload to choose a file.',
    });
  }, []);

  const startPaymentCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      notifyCameraPermission(
        'Camera is not available. Turn off Camera upload to upload a receipt image from files.'
      );
      return;
    }
    try {
      if (paymentStream) {
        paymentStream.getTracks().forEach(track => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setPaymentStream(mediaStream);
      setPaymentCameraOpen(true);
      setTimeout(() => {
        if (paymentReceiptVideoRef.current) {
          paymentReceiptVideoRef.current.srcObject = mediaStream;
          paymentReceiptVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (error) {
      notifyCameraPermission(
        'Camera access was denied. Allow camera permission, or turn off Camera upload for this receipt.'
      );
    }
  };

  const stopPaymentCamera = () => {
    if (paymentStream) {
      paymentStream.getTracks().forEach(track => track.stop());
      setPaymentStream(null);
    }
    setPaymentCameraOpen(false);
  };

  const capturePaymentReceipt = () => {
    if (paymentReceiptVideoRef.current && paymentReceiptCanvasRef.current) {
      const video = paymentReceiptVideoRef.current;
      const canvas = paymentReceiptCanvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setPaymentReceipt(imageData);
      stopPaymentCamera();
    }
  };

  const handlePaymentReceiptUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPaymentReceipt(event.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please upload an image file.');
      }
    }
  };

  const handlePaymentReceiptClick = () => {
    if (paymentCameraUpload) {
      startPaymentCamera();
    } else {
      paymentReceiptFileInputRef.current?.click();
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) {
      setError('Please enter a valid payment amount.');
      return;
    }
    if (!paymentMethod) {
      setError('Please select a payment method.');
      return;
    }
    if (paymentMethod === 'PAYMENT LINK') {
      setError('Use Email, WhatsApp, or Both in the payment link panel.');
      return;
    }

    setSubmittingPayment(true);
    setError(null);
    try {
      const processedAtIso = toPaymentProcessedAtIso(paymentDate);
      // Check if payment API supports document upload
      // For now, try with JSON first, then FormData if document is present
      let response;
      if (paymentReceipt) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('booking', bookingId);
        formData.append('amount', paymentAmount);
        formData.append('payment_type', 'ADVANCE');
        formData.append('payment_method', paymentMethod);
        if (referenceNumber) {
          formData.append('reference_number', referenceNumber);
        }
        formData.append('status', 'SUCCESS');
        if (processedAtIso) formData.append('processed_at', processedAtIso);
        
        // Add receipt if available
        if (paymentReceipt.startsWith('data:image')) {
          // Convert data URL to blob
          const blobResponse = await fetch(paymentReceipt);
          const blob = await blobResponse.blob();
          formData.append('document', blob, 'payment_receipt.jpg');
        } else if (paymentReceipt instanceof File) {
          // It's already a file
          formData.append('document', paymentReceipt);
        }

        response = await api.post('/finance/api/payments/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use JSON for regular payment
        response = await api.post('/finance/api/payments/', {
          booking: bookingId,
          amount: paymentAmount,
          payment_type: 'ADVANCE',
          payment_method: paymentMethod,
          reference_number: referenceNumber || null,
          status: 'SUCCESS',
          processed_at: processedAtIso,
        });
      }

      if (response.data) {
        // Reset form
        setPaymentAmount(0);
        setPaymentMethod('');
        setReferenceNumber('');
        setPaymentReceipt(null);
        setPaymentCameraUpload(false);
        setPaymentLinkSendOpen(false);
        setError(null);
        setPaymentDrawerOpen(false);
        setSubmittingPayment(false);
        markBookingModified();
        // Patch list card immediately; refresh drawer data in background.
        onStatusUpdate?.(getSafeBookingId());
        void (async () => {
          try {
            await Promise.all([fetchPaymentHistory(), fetchBookingDetails()]);
          } catch {
            // Keep drawer usable; card already patched.
          }
        })();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to process payment.');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleMarkAsNoShow = async () => {
    setUpdating(true);
    setError(null);
    try {
      const result = await markBookingNoShow(bookingId);
      setConfirmDialogOpen(false);
      await fetchBookingDetails();
      markBookingModified();
      if (result?.needs_retry || result?.no_show_status === 'pending') {
        setError(result?.message || 'Rooms were released, but the OTA did not accept the no-show. Retry from No Show.');
        return;
      }
      handleDrawerClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update booking status');
    } finally {
      setUpdating(false);
    }
  };

  // Helper function to find booking item ID for a guest (reused for edit and delete)
  const findBookingItemIdForGuest = (guest) => {
    if (!guest || !booking) return null;
    
    const bookingItems = booking?.items || booking?.booking_items || [];
    
    // Try multiple matching strategies (same as handleEditGuest)
    for (const item of bookingItems) {
      if (item.guests && Array.isArray(item.guests) && item.guests.length > 0) {
        // Strategy 1: Match by ID
        if (guest.id) {
          const foundById = item.guests.find(g => g.id && guest.id && g.id === guest.id);
          if (foundById) {
            return item.id;
          }
        }
        
        // Strategy 2: Match by name and email
        if (guest.first_name && guest.last_name && guest.email) {
          const foundByNameEmail = item.guests.find(g => 
            g.first_name === guest.first_name && 
            g.last_name === guest.last_name && 
            g.email === guest.email
          );
          if (foundByNameEmail) {
            return item.id;
          }
        }
        
        // Strategy 3: Match by phone number
        if (guest.phone) {
          const guestPhone = guest.phone.toString().replace(/\D/g, '');
          const foundByPhone = item.guests.find(g => {
            if (!g.phone) return false;
            const gPhone = g.phone.toString().replace(/\D/g, '');
            return gPhone === guestPhone;
          });
          if (foundByPhone) {
            return item.id;
          }
        }
        
        // Strategy 4: Match by name only (if no email/phone)
        if (guest.first_name && guest.last_name) {
          const foundByName = item.guests.find(g => 
            g.first_name === guest.first_name && 
            g.last_name === guest.last_name
          );
          if (foundByName) {
            return item.id;
          }
        }
      }
    }
    
    // Fallback: use the first booking item if available
    if (bookingItems.length > 0) {
      return bookingItems[0].id;
    }
    
    return null;
  };

  const handleDeleteGuest = async () => {
    if (!guestToDelete || !guestToDelete.guest?.id || !guestToDelete.bookingItemId) {
      setError('Invalid guest information.');
      setDeleteGuestDialogOpen(false);
      return;
    }
    
    setUpdating(true);
    setError(null);
    try {
      const response = await api.patch(`/booking/api/${bookingId}/`, {
        booking_item_id: guestToDelete.bookingItemId,
        remove_guest_id: guestToDelete.guest.id,
      });
      
      if (response.data.success) {
        setDeleteGuestDialogOpen(false);
        setGuestToDelete(null);
        // Refresh booking details
        await fetchBookingDetails();
        markBookingModified();
        // Reset slider indices - booking will be refreshed so indices will be recalculated
        setGuestSlideIndex({});
      } else {
        setError(response.data.error || 'Failed to remove guest.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to remove guest.');
    } finally {
      setUpdating(false);
    }
  };

  const handleEditGuest = async (guestToEdit = null) => {
    // Get guest from parameter, then selectedGuestForAction, then try to find from booking
    let guest = guestToEdit || selectedGuestForAction;
    
    // If still no guest, try to find from booking items
    if (!guest && booking) {
      const bookingItems = booking?.items || booking?.booking_items || [];
      for (const item of bookingItems) {
        if (item.guests && Array.isArray(item.guests) && item.guests.length > 0) {
          // Use the first guest from the first item as fallback
          guest = item.guests[0];
          break;
        }
      }
    }
    
    if (!guest) {setError('No guest selected for editing. Please select a guest first.');
      return;
    }
    
    // Find booking item for this guest - check both 'items' and 'booking_items'
    let bookingItemId = null;
    const bookingItems = booking?.items || booking?.booking_items || [];
    
    // Try multiple matching strategies
    for (const item of bookingItems) {
      if (item.guests && Array.isArray(item.guests) && item.guests.length > 0) {
        // Strategy 1: Match by ID
        if (guest.id) {
          const foundById = item.guests.find(g => g.id && guest.id && g.id === guest.id);
          if (foundById) {
            bookingItemId = item.id;
            break;
          }
        }
        
        // Strategy 2: Match by name and email
        if (!bookingItemId && guest.first_name && guest.last_name && guest.email) {
          const foundByNameEmail = item.guests.find(g => 
            g.first_name === guest.first_name && 
            g.last_name === guest.last_name && 
            g.email === guest.email
          );
          if (foundByNameEmail) {
            bookingItemId = item.id;
            break;
          }
        }
        
        // Strategy 3: Match by phone number
        if (!bookingItemId && guest.phone) {
          const guestPhone = guest.phone.toString().replace(/\D/g, '');
          const foundByPhone = item.guests.find(g => {
            if (!g.phone) return false;
            const gPhone = g.phone.toString().replace(/\D/g, '');
            return gPhone === guestPhone;
          });
          if (foundByPhone) {
            bookingItemId = item.id;
            break;
          }
        }
        
        // Strategy 4: Match by name only (if no email/phone)
        if (!bookingItemId && guest.first_name && guest.last_name) {
          const foundByName = item.guests.find(g => 
            g.first_name === guest.first_name && 
            g.last_name === guest.last_name
          );
          if (foundByName) {
            bookingItemId = item.id;
            break;
          }
        }
      }
    }
    
    // If still not found, use the first booking item as fallback (for adding new guest to existing item)
    if (!bookingItemId && bookingItems.length > 0) {
      bookingItemId = bookingItems[0].id;}
    
    if (!bookingItemId) {
      setError('Unable to find booking item for this guest. Please ensure the booking has at least one item.');
      setSelectedGuestForAction(null);
      return;
    }
    
    // Parse phone and country_code from guest.phone
    let phoneNumber = '';
    let countryCode = '+91'; // default
    if (guest.phone) {
      const phoneStr = guest.phone.toString();
      if (phoneStr.includes('-')) {
        const parts = phoneStr.split('-');
        if (parts.length >= 2) {
          countryCode = parts[0].startsWith('+') ? parts[0] : '+' + parts[0];
          phoneNumber = parts.slice(1).join('').replace(/\D/g, '');
        } else {
          phoneNumber = phoneStr.replace(/\D/g, '');
        }
      } else if (phoneStr.startsWith('+')) {
        const match = phoneStr.match(/^(\+\d{1,3})(\d+)$/);
        if (match) {
          countryCode = match[1];
          phoneNumber = match[2];
        } else {
          phoneNumber = phoneStr.replace(/\D/g, '');
        }
      } else {
        phoneNumber = phoneStr.replace(/\D/g, '');
      }
    }
    if (guest.country_code) {
      countryCode = guest.country_code.startsWith('+') ? guest.country_code : '+' + guest.country_code;
    }
    
    // Get document URLs - from guest object or fetch via API if guest has documents but no URLs
    let idFrontUrl = '';
    let idBackUrl = '';
    const frontUrlField = guest.id_front_url || guest.document_front_url || guest.id_front || guest.id_front_image || null;
    const backUrlField = guest.id_back_url || guest.document_back_url || guest.id_back || guest.id_back_image || null;
    
    if (frontUrlField) {
      const frontUrl = String(frontUrlField);
      if (frontUrl.startsWith('http') || frontUrl.startsWith('data:')) {
        idFrontUrl = frontUrl;
      } else {
        idFrontUrl = frontUrl.startsWith('/')
          ? `${window.location.origin}${frontUrl}`
          : `${window.location.origin}/${frontUrl}`;
      }
    }
    if (backUrlField) {
      const backUrl = String(backUrlField);
      if (backUrl.startsWith('http') || backUrl.startsWith('data:')) {
        idBackUrl = backUrl;
      } else {
        idBackUrl = backUrl.startsWith('/')
          ? `${window.location.origin}${backUrl}`
          : `${window.location.origin}/${backUrl}`;
      }
    }
    
    // Fetch document URLs from API when guest has documents but URLs not in guest object
    if ((!idFrontUrl || !idBackUrl) && (guest.has_id_front || guest.has_id_back) && guest.id && (booking?.id || bookingId)) {
      try {
        const docData = await getBookingGuestDocuments(booking?.id || bookingId, guest.id);
        if (docData?.id_front_url) idFrontUrl = String(docData.id_front_url);
        if (docData?.id_back_url) idBackUrl = String(docData.id_back_url);
      } catch (err) {
        // Non-blocking: form still opens, just without preloaded images
      }
    }
    
    // Normalize country: API may return code (e.g. "IN") or name (e.g. "India") - Autocomplete needs name
    let countryName = guest.country || '';
    if (countryName) {
      const countryObj = countriesData.countries.find(c => c.code === countryName);
      if (countryObj) {
        countryName = countryObj.name;
      } else {
        const byName = countriesData.countries.find(c => c.name === countryName);
        if (byName) countryName = byName.name;
      }
    }
    
    // Combine first_name, middle_name, last_name into single name field
    const fullName = [
      guest.first_name || '',
      guest.middle_name || '',
      guest.last_name || ''
    ].filter(part => part && part.trim()).join(' ');
    
    setIndividualGuestFormData({
      title: guest.title || '',
      name: fullName,
      first_name: guest.first_name || '',
      middle_name: guest.middle_name || '',
      last_name: guest.last_name || '',
      email: guest.email || '',
      phone: phoneNumber,
      phone_country_code: countryCode,
      dob: guest.dob ? String(guest.dob).slice(0, 10) : '',
      gender: guest.gender || '',
      address: guest.address_line1 || guest.address || '',
      city: guest.city || '',
      state: guest.state || '',
      country: countryName || resolveHotelCountryName(),
      zipcode: guest.pincode || guest.zipcode || '',
      camera_upload: false,
      do_not_rent: guest.do_not_rent || false,
      do_not_rent_severity: guest.do_not_rent_severity || 'HIGH',
      do_not_rent_reason: guest.do_not_rent_reason || '',
      id_front: idFrontUrl,
      id_back: idBackUrl,
      document_type: guest.document_type || NO_DOCUMENT_VALUE,
      id_number: guest.id_number || '',
    });
    setForeignGuestDetails(mapGuestApiToForeignDetails(guest, countryName || resolveHotelCountryName()));
    setForeignGuestDrawerOpen(false);
    
    setEditingGuestItemId(bookingItemId);
    setEditingGuestId(guest.id || null);
    setStayGuestReplaceId(guest.id || null);
    setIsStayGuestEdit(true);
    // Allow phone search so an OTA stub can be mapped to an existing CRM guest.
    setIsPhoneLocked(false);
    setGuestSearchOptions([]);
    setValidationErrors({});
    setTouchedFields({});
    setGuestFormDrawerOpen(true);
    setSelectedGuestForAction(null);};

  const getBookingItemDateKey = useCallback((item) => {
    if (!item?.date) return '';
    return moment.isMoment(item.date)
      ? item.date.format('YYYY-MM-DD')
      : String(item.date).slice(0, 10);
  }, []);

  const isCheckedOutBookingItem = useCallback((item) => {
    const raw = item?.checkout;
    if (typeof raw === 'string') {
      return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
    }
    return raw === true || raw === 1;
  }, []);

  const pickPreferredGuestBookingItem = useCallback((items = []) => {
    const todayStr = moment().format('YYYY-MM-DD');
    const sortedItems = [...(items || [])].sort((a, b) => {
      const aDate = getBookingItemDateKey(a);
      const bDate = getBookingItemDateKey(b);
      if (aDate !== bDate) return aDate.localeCompare(bDate);
      return (a?.id || 0) - (b?.id || 0);
    });
    return (
      sortedItems.find((it) => !isCheckedOutBookingItem(it) && getBookingItemDateKey(it) === todayStr) ||
      sortedItems.find((it) => !isCheckedOutBookingItem(it) && getBookingItemDateKey(it) >= todayStr) ||
      sortedItems.find((it) => !isCheckedOutBookingItem(it)) ||
      sortedItems.find((it) => getBookingItemDateKey(it) >= todayStr) ||
      sortedItems[0] ||
      null
    );
  }, [getBookingItemDateKey, isCheckedOutBookingItem]);

  const handleAddGuest = (roomItems = null) => {
    const bookingItems = booking?.items || booking?.booking_items || [];
    const scopedItems = Array.isArray(roomItems) && roomItems.length > 0 ? roomItems : bookingItems;
    const targetItem = pickPreferredGuestBookingItem(scopedItems) || scopedItems[0];
    if (!targetItem) {
      setError('No booking items found for this room.');
      return;
    }

    // Cap by room-type max_guests (not booked adult count).
    const maxFromScoped = Math.max(
      0,
      ...scopedItems.map((it) => Number(it?.room_type?.max_guests) || 0),
    );
    const maxGuestsCap = Math.max(
      1,
      Number(targetItem?.room_type?.max_guests)
        || maxFromScoped
        || ((Number(targetItem?.room_type?.base_adults) || 0)
          + (Number(targetItem?.room_type?.base_children) || 0))
        || 4,
    );
    const existingGuestIds = new Set();
    scopedItems.forEach((it) => {
      (it.guests || []).forEach((g) => {
        if (g?.id != null) existingGuestIds.add(g.id);
      });
    });
    if (existingGuestIds.size >= maxGuestsCap) {
      setError(`This room type allows a maximum of ${maxGuestsCap} guest${maxGuestsCap === 1 ? '' : 's'}.`);
      return;
    }

    // Reset form data for new guest
    setIndividualGuestFormData({
      title: '',
      name: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      phone_country_code: '+91',
      dob: '',
      gender: '',
      address: '',
      city: '',
      state: '',
      country: resolveHotelCountryName(),
      zipcode: '',
      camera_upload: false,
      do_not_rent: false,
      do_not_rent_severity: 'HIGH',
      do_not_rent_reason: '',
      id_front: '',
      id_back: '',
      document_type: NO_DOCUMENT_VALUE,
      id_number: '',
    });
    setForeignGuestDetails(EMPTY_FOREIGN_GUEST_DETAILS);
    setForeignGuestDrawerOpen(false);

    setEditingGuestItemId(targetItem?.id || null);
    setEditingGuestId(null);
    setStayGuestReplaceId(null);
    setIsStayGuestEdit(false);
    setIsPhoneLocked(false);
    setGuestFormDrawerOpen(true);
    setSelectedGuestForAction(null);
    setValidationErrors({});
    setTouchedFields({});
    setGuestSearchOptions([]);
  };

  // Helper functions for guest drawer - from CheckInDrawer
  const countries = useMemo(() => countriesData.countries.map(country => ({
    code: country.code,
    name: country.name,
  })), []);

  const selectedCountryObj = useMemo(() => {
    if (!individualGuestFormData.country) return null;
    return countriesData.countries.find(c => c.name === individualGuestFormData.country || c.code === individualGuestFormData.country);
  }, [individualGuestFormData.country]);

  const hotelCountry = useMemo(() => resolveHotelCountryName(), []);
  const isForeignGuest = useMemo(
    () => isForeignGuestCountry(individualGuestFormData.country, hotelCountry),
    [individualGuestFormData.country, hotelCountry]
  );
  const foreignGuestComplete = useMemo(() => {
    if (!isForeignGuest) return true;
    return validateForeignGuestDetails(foreignGuestDetails).ok;
  }, [isForeignGuest, foreignGuestDetails]);

  const applyGuestCountryChange = useCallback((countryValue) => {
    const next = applyForeignCountryChange(countryValue, hotelCountry, foreignGuestDetails);
    setForeignGuestDetails(next.details);
    if (next.shouldOpenDrawer) {
      setIndividualGuestFormData((prev) => ({
        ...prev,
        document_type: prev.document_type === NO_DOCUMENT_VALUE ? 'passport' : prev.document_type,
      }));
      setForeignGuestDrawerOpen(true);
    } else {
      setForeignGuestDrawerOpen(false);
    }
    setValidationErrors((prev) => ({ ...prev, country: validateCountry(countryValue), foreignGuest: '' }));
  }, [foreignGuestDetails, hotelCountry]);

  const availableStates = useMemo(() => {
    if (!selectedCountryObj) return [];
    return selectedCountryObj.states.map(state => ({
      code: state.code,
      name: state.name,
    }));
  }, [selectedCountryObj]);

  const availableCities = useMemo(() => {
    if (!selectedCountryObj) return [];
    const selectedStateObj = selectedCountryObj.states.find(s => s.name === individualGuestFormData.state || s.code === individualGuestFormData.state);
    if (!selectedStateObj) return [];
    return selectedStateObj.cities || [];
  }, [selectedCountryObj, individualGuestFormData.state]);

  const countryCodes = useMemo(() => countryCodesData, []);

  const selectedPhoneCountryCode = useMemo(() => {
    return countryCodes.find(cc => cc.dialCode === individualGuestFormData.phone_country_code) || countryCodes.find(cc => cc.code === 'IN');
  }, [individualGuestFormData.phone_country_code, countryCodes]);

  const formatGuestSaveError = (rawError) => {
    const text = typeof rawError === 'string'
      ? rawError
      : JSON.stringify(rawError || '');
    if (
      text.includes('unique_guest_loyalty_per_hotel')
      || text.includes('Guest with this ID already exists')
      || text.includes('"id"')
    ) {
      return 'This guest or ID document already exists for this hotel. Please select the existing guest record or use a different ID number.';
    }
    return text || 'Failed to update guest information. Please try again.';
  };

  const documentUploadOptions = [
    { value: NO_DOCUMENT_VALUE, label: 'No Document' },
    { value: 'aadhaar', label: 'Aadhaar Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'voter_id', label: 'Voter ID (EPIC)' },
    { value: 'driving_license', label: 'Driving Licence' },
    { value: 'pan', label: 'PAN Card' },
    { value: 'government_id', label: 'Government Issued ID' },
    { value: 'oci', label: 'OCI Card' },
    { value: 'pio', label: 'PIO Card' },
    { value: 'student_id', label: 'Student ID' },
    { value: 'company_id', label: 'Corporate / Company ID' },
    { value: 'other', label: 'Other Document' },
  ];

  const severityOptions = [
    { value: 'LOW', label: 'Low' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HIGH', label: 'High' },
    { value: 'CRITICAL', label: 'Critical' },
  ];

  const titleOptions = [
    { value: 'Mr', label: 'Mr' },
    { value: 'Mrs', label: 'Mrs' },
    { value: 'Ms', label: 'Ms' },
    { value: 'Dr', label: 'Dr' },
    { value: 'Prof', label: 'Prof' },
    { value: 'Sir', label: 'Sir' },
    { value: 'Madam', label: 'Madam' },
  ];

  const getDocumentTypeLabel = () => {
    if (!individualGuestFormData.document_type) return 'Document';
    const docType = documentUploadOptions.find(opt => opt.value === individualGuestFormData.document_type);
    return docType ? docType.label : 'Document';
  };
  
  const getFrontDocumentLabel = () => {
    const docLabel = getDocumentTypeLabel();
    return `Upload ${docLabel} Front`;
  };
  
  const getBackDocumentLabel = () => {
    const docLabel = getDocumentTypeLabel();
    return `Upload ${docLabel} Back`;
  };

  const getDocumentNumberPlaceholder = () => {
    const type = individualGuestFormData.document_type;
    if (type === 'aadhaar') return 'XXXX-XXXX-XXXX';
    if (type === 'passport') return 'Enter passport number (e.g. A1234567)';
    if (type === 'pan') return 'Enter PAN (e.g. ABCDE1234F)';
    if (type === 'voter_id') return 'Enter voter ID (e.g. ABC1234567)';
    if (type === 'driving_license') return 'Enter driving licence number';
    return 'Enter ID number';
  };

  const isNoDocumentSelected = individualGuestFormData.document_type === NO_DOCUMENT_VALUE;
  const effectiveDocumentType = isNoDocumentSelected ? '' : individualGuestFormData.document_type;
  const isAadhaarDocumentSelected = isAadhaarDocumentType(effectiveDocumentType);
  const normalizedIdNumberValue = isAadhaarDocumentSelected
    ? formatAadhaarDisplay(individualGuestFormData.id_number)
    : (individualGuestFormData.id_number || '');

  const handleAiAddGuestSuccess = useCallback((guest, previews) => {
    const g = guest || {};
    const p = typeof previews === 'string'
      ? { frontDataUrl: previews, backDataUrl: null }
      : (previews || {});
    const firstName = String(g.guest_first_name || '').trim();
    const middleName = String(g.guest_middle_name || '').trim();
    const lastName = String(g.guest_last_name || '').trim();
    const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ')
      || String(g.guest_name || g.name || '').trim();
    const docType = String(g.upload_document_type || g.document_type || 'aadhaar').trim() || 'aadhaar';
    const dobRaw = g.guest_dob || g.dob || g.date_of_birth || '';
    const dob = dobRaw ? String(dobRaw).trim().slice(0, 10) : '';
    const gender = String(g.guest_gender || g.gender || '').trim().toLowerCase();

    setIndividualGuestFormData((prev) => ({
      ...prev,
      title: String(g.guest_title || prev.title || '').trim(),
      name: fullName || prev.name,
      first_name: firstName || prev.first_name,
      middle_name: middleName || prev.middle_name,
      last_name: lastName || prev.last_name,
      email: String(g.guest_email || prev.email || '').trim(),
      phone: prev.phone,
      dob: dob || prev.dob,
      gender: gender || prev.gender,
      address: String(g.guest_address || prev.address || '').trim(),
      city: String(g.guest_city || prev.city || '').trim(),
      state: String(g.guest_state || prev.state || '').trim(),
      country: String(g.guest_country || g.nationality || prev.country || '').trim(),
      zipcode: String(g.guest_pincode || prev.zipcode || '').trim(),
      document_type: docType,
      id_number: g.document_id_number != null
        ? normalizeDocumentIdInput(docType, g.document_id_number)
        : prev.id_number,
      id_front: p.frontDataUrl || prev.id_front,
      id_back: p.backDataUrl || prev.id_back,
    }));
    setTouchedFields((prev) => ({
      ...prev,
      name: true,
      id_number: true,
      document_type: true,
    }));
    setValidationErrors((prev) => ({
      ...prev,
      name: '',
      id_number: '',
    }));
    setIsPhoneLocked(false);
    setEditingGuestId(null);
    setStayGuestReplaceId(null);
    setIsStayGuestEdit(false);
    setGuestSearchOptions([]);
    setAiAddGuestOpen(false);
    applyGuestCountryChange(String(g.guest_country || g.nationality || '').trim());
  }, [applyGuestCountryChange]);

  const parseFullName = (fullName) => {
    if (!fullName || !fullName.trim()) {
      return { first_name: '', middle_name: '', last_name: '' };
    }
    const normalizedName = fullName.trim().replace(/\s+/g, ' ');
    const nameParts = normalizedName.split(' ').filter(part => part.length > 0);
    if (nameParts.length === 0) {
      return { first_name: '', middle_name: '', last_name: '' };
    } else if (nameParts.length === 1) {
      return { first_name: nameParts[0], middle_name: '', last_name: '' };
    } else if (nameParts.length === 2) {
      return { first_name: nameParts[0], middle_name: '', last_name: nameParts[1] };
    } else if (nameParts.length === 3) {
      return { first_name: nameParts[0], middle_name: nameParts[1], last_name: nameParts[2] };
    } else {
      const firstName = nameParts.slice(0, nameParts.length - 2).join(' ');
      const middleName = nameParts[nameParts.length - 2];
      const lastName = nameParts[nameParts.length - 1];
      return { first_name: firstName, middle_name: middleName, last_name: lastName };
    }
  };

  const validateEmail = (email) => {
    if (!email || !email.trim()) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhone = (phone, countryCode = '+91') => {
    return validatePhoneForCountry(phone, countryCode);
  };

  const validateName = (name) => {
    if (!name || !name.trim()) {
      return 'Name is required';
    }
    if (name.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (name.trim().length > 100) {
      return 'Name cannot exceed 100 characters';
    }
    const nameRegex = /^[a-zA-Z\s\-'\.]+$/;
    if (!nameRegex.test(name.trim())) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
  };

  const validatePincode = (pincode) => {
    if (!pincode || !pincode.trim()) return '';
    const digitsOnly = pincode.replace(/\D/g, '');
    if (digitsOnly.length < 4) {
      return 'Pincode must be at least 4 digits';
    }
    if (digitsOnly.length > 10) {
      return 'Pincode cannot exceed 10 digits';
    }
    return '';
  };

  const validateCity = () => {
    return '';
  };

  const validateCountry = (country) => {
    if (!country || !country.trim()) {
      return 'Country is required';
    }
    return '';
  };

  const validateDocumentIdNumber = (documentType, idNumber) => {
    const docType = (documentType || '').trim();
    const raw = (idNumber || '').trim();

    if (!docType && !raw) return '';
    if (!docType && raw) return 'Select document type before entering ID number';
    if (!raw) return 'ID number is required';

    const normalized = raw.replace(/\s+/g, '').toUpperCase();

    if (docType === 'aadhaar') {
      return validateAadhaarIdNumber(normalized);
    }
    if (docType === 'passport') {
      return isValidPassportIdNumber(normalized)
        ? ''
        : 'Passport number must be 5-20 letters/numbers (e.g. A1234567)';
    }
    if (docType === 'voter_id') {
      return /^[A-Z]{3}[0-9]{7}$/.test(normalized)
        ? ''
        : 'Voter ID must be 3 letters followed by 7 digits (e.g. ABC1234567)';
    }
    if (docType === 'pan') {
      return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalized)
        ? ''
        : 'PAN must be 5 letters, 4 digits, and 1 letter (e.g. ABCDE1234F)';
    }
    if (docType === 'driving_license') {
      return /^[A-Z0-9-]{10,20}$/.test(normalized)
        ? ''
        : 'Driving licence number must be 10-20 characters';
    }
    return /^[A-Z0-9][A-Z0-9\-/]{3,29}$/.test(normalized)
      ? ''
      : 'ID number must be 4-30 characters (letters, numbers, -, /)';
  };

  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    if (fieldName === 'phone') {
      const error = validatePhone(individualGuestFormData.phone, individualGuestFormData.phone_country_code);
      setValidationErrors(prev => ({ ...prev, phone: error }));
    } else if (fieldName === 'email') {
      const error = validateEmail(individualGuestFormData.email);
      setValidationErrors(prev => ({ ...prev, email: error }));
    } else if (fieldName === 'name' || fieldName === 'first_name') {
      const nameToValidate = fieldName === 'name' 
        ? individualGuestFormData.name 
        : individualGuestFormData.first_name || individualGuestFormData.name;
      const error = validateName(nameToValidate);
      setValidationErrors(prev => ({ ...prev, first_name: error }));
    } else if (fieldName === 'pincode' || fieldName === 'zipcode') {
      const error = validatePincode(individualGuestFormData.zipcode);
      setValidationErrors(prev => ({ ...prev, zipcode: error }));
    } else if (fieldName === 'city') {
      const hasAddress = !!(individualGuestFormData.address && individualGuestFormData.address.trim());
      const error = validateCity(individualGuestFormData.city, hasAddress);
      setValidationErrors(prev => ({ ...prev, city: error }));
    } else if (fieldName === 'country') {
      const error = validateCountry(individualGuestFormData.country);
      setValidationErrors(prev => ({ ...prev, country: error }));
    } else if (fieldName === 'address') {
      const hasAddress = !!(individualGuestFormData.address && individualGuestFormData.address.trim());
      if (touchedFields.city) {
        const cityError = validateCity(individualGuestFormData.city, hasAddress);
        setValidationErrors(prev => ({ ...prev, city: cityError }));
      }
      if (touchedFields.country) {
        const countryError = validateCountry(individualGuestFormData.country);
        setValidationErrors(prev => ({ ...prev, country: countryError }));
      }
    } else if (fieldName === 'id_number') {
      const error = validateDocumentIdNumber(
        effectiveDocumentType,
        individualGuestFormData.id_number
      );
      setValidationErrors(prev => ({ ...prev, id_number: error }));
    } else if (fieldName === 'document_type') {
      const error = validateDocumentIdNumber(
        effectiveDocumentType,
        individualGuestFormData.id_number
      );
      setValidationErrors(prev => ({ ...prev, id_number: error }));
    }
  };

  const handleNameChange = (value) => {
    setIndividualGuestFormData(prev => ({ ...prev, name: value }));
    const { first_name, middle_name, last_name } = parseFullName(value);
    setIndividualGuestFormData(prev => ({ 
      ...prev, 
      name: value,
      first_name,
      middle_name,
      last_name,
    }));
    if (touchedFields.name || touchedFields.first_name) {
      const error = validateName(first_name || value);
      setValidationErrors(prev => ({ ...prev, first_name: error }));
    }
  };

  const handleEmailChange = (value) => {
    setIndividualGuestFormData(prev => ({ ...prev, email: value }));
    if (touchedFields.email) {
      const error = validateEmail(value);
      setValidationErrors(prev => ({ ...prev, email: error }));
    }
  };

  const handlePhoneChange = (value) => {
    const dialCode = individualGuestFormData.phone_country_code || '+91';
    const digitsOnly = normalizePhoneInputForCountry(value, dialCode);
    setIndividualGuestFormData((prev) => ({ ...prev, phone: digitsOnly }));
    if (touchedFields.phone) {
      const error = validatePhone(digitsOnly, dialCode);
      setValidationErrors((prev) => ({ ...prev, phone: error }));
    }
    if (phoneSearchDebounceTimer) {
      clearTimeout(phoneSearchDebounceTimer);
    }
    // Stay-guest edit: keep current id unless staff selects a CRM match.
    if (isStayGuestEdit) {
      const maxLen = getPhoneMaxLength(dialCode);
      if (digitsOnly && digitsOnly.length >= Math.min(6, maxLen)) {
        const timer = setTimeout(() => {
          handleAutoSearchGuest(digitsOnly);
        }, 500);
        setPhoneSearchDebounceTimer(timer);
      } else {
        setGuestSearchOptions([]);
      }
      return;
    }
    // Add-guest flow: typing a new number drops a prior search selection.
    if (editingGuestId) {
      setEditingGuestId(null);
      setIsPhoneLocked(false);
    }
    const maxLen = getPhoneMaxLength(dialCode);
    // Search when enough digits for the selected country (capped by country max).
    if (digitsOnly && digitsOnly.length >= Math.min(6, maxLen)) {
      const timer = setTimeout(() => {
        handleAutoSearchGuest(digitsOnly);
      }, 500);
      setPhoneSearchDebounceTimer(timer);
    } else {
      setGuestSearchOptions([]);
    }
  };

  const resolveGuestSearchOption = useCallback((value) => {
    if (value && typeof value === 'object' && value.guest) return value;
    if (typeof value === 'string' && value.trim()) {
      const needle = value.trim().toLowerCase();
      const digits = needle.replace(/\D/g, '');
      return guestSearchOptions.find((opt) => {
        const optPhone = String(opt.phone || '').replace(/\D/g, '');
        const optName = String(opt.name || '').toLowerCase();
        return (digits && optPhone && (optPhone === digits || optPhone.endsWith(digits)))
          || optName === needle;
      }) || null;
    }
    return null;
  }, [guestSearchOptions]);

  const applySelectedGuestToForm = useCallback((option) => {
    if (!option?.guest) return;
    const guest = option.guest;
    let phoneNumber = (option.phone || '').toString().replace(/\D/g, '') || '';
    let countryCode = '+91';
    if (guest.phone) {
      const phoneStr = guest.phone.toString();
      if (phoneStr.includes('-')) {
        const parts = phoneStr.split('-');
        if (parts.length >= 2) {
          countryCode = parts[0].startsWith('+') ? parts[0] : `+${parts[0]}`;
          if (!phoneNumber) phoneNumber = parts.slice(1).join('').replace(/\D/g, '');
        }
      } else if (phoneStr.startsWith('+')) {
        const match = phoneStr.match(/^(\+\d{1,3})(\d+)$/);
        if (match) {
          countryCode = match[1];
          if (!phoneNumber) phoneNumber = match[2];
        } else if (!phoneNumber) {
          phoneNumber = phoneStr.replace(/\D/g, '');
        }
      } else if (!phoneNumber) {
        phoneNumber = phoneStr.replace(/\D/g, '');
      }
    }
    if (guest.country_code) {
      countryCode = guest.country_code.startsWith('+')
        ? guest.country_code
        : `+${guest.country_code}`;
    }
    const idFrontUrl = toGuestDocUrl(
      guest.id_front_url || guest.id_front || guest.document_front_url,
    );
    const idBackUrl = toGuestDocUrl(
      guest.id_back_url || guest.id_back || guest.document_back_url,
    );
    const fullName = [guest.first_name, guest.middle_name, guest.last_name]
      .filter((part) => part && String(part).trim())
      .join(' ');
    const dobRaw = guest.dob || guest.date_of_birth || '';
    const dob = dobRaw ? String(dobRaw).slice(0, 10) : '';
    const gender = String(guest.gender || '').trim().toLowerCase();
    const countryObj = guest.country
      ? countriesData.countries.find(
          (c) =>
            c.code === guest.country
            || c.name === guest.country
            || String(c.name).toLowerCase() === String(guest.country).toLowerCase(),
        )
      : null;
    const countryName = countryObj ? countryObj.name : (guest.country || '');
    const stateObj = countryObj?.states?.find(
      (s) =>
        s.name === guest.state
        || s.code === guest.state
        || String(s.name).toLowerCase() === String(guest.state || '').toLowerCase(),
    );
    const stateName = stateObj ? stateObj.name : (guest.state || '');
    const cityList = stateObj?.cities || [];
    const cityName = cityList.find(
      (c) => String(c).toLowerCase() === String(guest.city || '').toLowerCase(),
    ) || (guest.city || '');
    const documentType = String(guest.document_type || '').trim() || NO_DOCUMENT_VALUE;
    setIndividualGuestFormData((prev) => ({
      ...prev,
      phone: phoneNumber || prev.phone,
      phone_country_code: countryCode || prev.phone_country_code,
      title: guest.title || '',
      name: guest.name || fullName || '',
      first_name: guest.first_name || '',
      middle_name: guest.middle_name || '',
      last_name: guest.last_name || '',
      email: guest.email || '',
      dob,
      gender,
      address: guest.address_line1 || guest.address || '',
      zipcode: guest.pincode || guest.zipcode || '',
      country: countryName,
      state: stateName,
      city: cityName,
      do_not_rent: Boolean(guest.do_not_rent),
      do_not_rent_severity: guest.do_not_rent_severity || 'HIGH',
      do_not_rent_reason: guest.do_not_rent_reason || '',
      document_type: documentType,
      id_number: guest.id_number || '',
      id_front: idFrontUrl || '',
      id_back: idBackUrl || '',
    }));
    setForeignGuestDetails(mapGuestApiToForeignDetails(guest, countryName));
    setForeignGuestDrawerOpen(false);
    setEditingGuestId(guest.id || null);
    setIsPhoneLocked(Boolean(guest.id));
    setGuestSearchOptions([]);
    if (stateName || cityName) {
      window.setTimeout(() => {
        setIndividualGuestFormData((prev) => ({
          ...prev,
          state: stateName || prev.state,
          city: cityName || prev.city,
        }));
      }, 150);
    }
    setValidationErrors((prev) => ({
      ...prev,
      name: '',
      phone: '',
      id_number: '',
      country: '',
      city: '',
      ...(dob ? { dob: '' } : {}),
      ...(gender ? { gender: '' } : {}),
    }));
    setTouchedFields((prev) => ({
      ...prev,
      name: true,
      phone: true,
      id_number: true,
      document_type: true,
      country: true,
      ...(dob ? { dob: true } : {}),
      ...(gender ? { gender: true } : {}),
    }));
  }, []);

  const handleAutoSearchGuest = async (phoneValue) => {
    if (!phoneValue || !phoneValue.trim() || phoneValue.trim().length < 6) {
      setGuestSearchOptions([]);
      return;
    }
    setSearching(true);
    try {
      const phoneToSearch = phoneValue.trim().replace(/\D/g, '');
      const response = await searchGuestByPhone(phoneToSearch);
      if (response.guest) {
        const guest = response.guest;
        let fullGuestData = guest;
        const searchDocumentUrls = {
          id_front_url: guest.id_front_url,
          id_back_url: guest.id_back_url,
          document_type: guest.document_type,
        };
        try {
          const customerDetailResponse = await getCustomerDetail(guest.id);
          if (customerDetailResponse && customerDetailResponse.customer) {
            fullGuestData = customerDetailResponse.customer;
            fullGuestData.id_front_url = searchDocumentUrls.id_front_url || fullGuestData.id_front_url || null;
            fullGuestData.id_back_url = searchDocumentUrls.id_back_url || fullGuestData.id_back_url || null;
            fullGuestData.document_type = searchDocumentUrls.document_type || fullGuestData.document_type || null;
          }
        } catch (err) {}
        const fullName = [fullGuestData.first_name, fullGuestData.middle_name, fullGuestData.last_name]
          .filter((part) => part && part.trim())
          .join(' ');
        setGuestSearchOptions([{
          id: fullGuestData.id,
          name: fullName || fullGuestData.name || phoneToSearch,
          phone: fullGuestData.phone || phoneToSearch,
          email: fullGuestData.email || '',
          guest: fullGuestData,
        }]);
      } else {
        setGuestSearchOptions([]);
      }
    } catch (err) {
      setGuestSearchOptions([]);
    } finally {
      setSearching(false);
    }
  };

  const clearDocumentImageError = () => {
    setValidationErrors((prev) =>
      prev.id_front || prev.id_back ? { ...prev, id_front: '', id_back: '' } : prev,
    );
    setTouchedFields((prev) => ({ ...prev, id_front: true, id_back: true }));
  };

  const syncDocumentImageErrors = (idFront, idBack) => {
    const imageErrors = validateGuestDocumentImages(
      individualGuestFormData.document_type,
      idFront,
      idBack,
    );
    setTouchedFields((prev) => ({ ...prev, id_front: true, id_back: true }));
    setValidationErrors((prev) => ({
      ...prev,
      id_front: imageErrors.id_front,
      id_back: imageErrors.id_back,
    }));
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'front') {
          setIndividualGuestFormData(prev => ({ ...prev, id_front: reader.result }));
        } else {
          setIndividualGuestFormData(prev => ({ ...prev, id_back: reader.result }));
        }
        clearDocumentImageError();
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async (type) => {
    try {
      const nativeImageData = await captureNativeCameraDataUrl();
      if (nativeImageData) {
        if (type === 'front') {
          setIndividualGuestFormData(prev => ({ ...prev, id_front: nativeImageData }));
        } else {
          setIndividualGuestFormData(prev => ({ ...prev, id_back: nativeImageData }));
        }
        clearDocumentImageError();
        return;
      }
    } catch (error) {
      const message = getNativeCameraErrorMessage(error);
      if (message) notifyCameraPermission(message);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      notifyCameraPermission(
        'Camera is not available. Turn off Camera upload to upload ID images from files.'
      );
      return;
    }
    try {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setStream(mediaStream);
      setCameraType(type);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (error) {
      notifyCameraPermission(
        'Camera access was denied. Allow camera permission for this site, or turn off Camera upload to choose a file.'
      );
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
    setCameraType(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      if (cameraType === 'front') {
        setIndividualGuestFormData(prev => ({ ...prev, id_front: imageData }));
      } else {
        setIndividualGuestFormData(prev => ({ ...prev, id_back: imageData }));
      }
      clearDocumentImageError();
      stopCamera();
    }
  };

  const handleUploadClick = (type) => {
    if (individualGuestFormData.camera_upload) {
      startCamera(type);
    } else {
      if (type === 'front') {
        fileInputFrontRef.current?.click();
      } else {
        fileInputBackRef.current?.click();
      }
    }
  };

  const handleSaveIndividualGuest = async () => {
    if (!bookingId) return;
    setSavingGuest(true);
    setGuestFormError(null);
    try {
      let itemsToUpdate = [];
      if (editingGuestItemId) {
        const bookingItems = booking?.items || booking?.booking_items || [];
        const item = bookingItems.find((i) => i.id === editingGuestItemId);
        if (!item) {
          setGuestFormError('Booking item not found.');
          setSavingGuest(false);
          return;
        }
        itemsToUpdate = [item];
      } else {
        const bookingItems = booking?.items || booking?.booking_items || [];
        if (bookingItems.length > 0) {
          const preferredItem = pickPreferredGuestBookingItem(bookingItems);
          if (preferredItem) {
            itemsToUpdate = [preferredItem];
          }
        }
      }
      if (itemsToUpdate.length === 0) {
        setGuestFormError('No booking items found.');
        setSavingGuest(false);
        return;
      }
      let phoneNumber = null;
      let countryCode = null;
      if (individualGuestFormData.phone && individualGuestFormData.phone.trim()) {
        phoneNumber = individualGuestFormData.phone.trim().replace(/\D/g, '');
        countryCode = individualGuestFormData.phone_country_code || '+91';
        if (!countryCode.startsWith('+')) {
          countryCode = '+' + countryCode;
        }
      }
      let firstName = individualGuestFormData.first_name;
      let middleName = individualGuestFormData.middle_name;
      let lastName = individualGuestFormData.last_name;
      if (individualGuestFormData.name && individualGuestFormData.name.trim()) {
        const parsed = parseFullName(individualGuestFormData.name);
        firstName = parsed.first_name;
        middleName = parsed.middle_name;
        lastName = parsed.last_name;
      }
      const errors = {};
      const nameError = validateName(firstName || individualGuestFormData.name);
      if (nameError) {
        errors.first_name = nameError;
        setTouchedFields(prev => ({ ...prev, name: true, first_name: true }));
      }
      const phoneError = validatePhone(individualGuestFormData.phone, countryCode || individualGuestFormData.phone_country_code);
      if (phoneError) {
        errors.phone = phoneError;
        setTouchedFields(prev => ({ ...prev, phone: true }));
      }
      if (individualGuestFormData.email && individualGuestFormData.email.trim()) {
        const emailError = validateEmail(individualGuestFormData.email);
        if (emailError) {
          errors.email = emailError;
          setTouchedFields(prev => ({ ...prev, email: true }));
        }
      }
      if (individualGuestFormData.zipcode && individualGuestFormData.zipcode.trim()) {
        const pincodeError = validatePincode(individualGuestFormData.zipcode);
        if (pincodeError) {
          errors.zipcode = pincodeError;
          setTouchedFields(prev => ({ ...prev, zipcode: true }));
        }
      }
      const countryError = validateCountry(individualGuestFormData.country);
      if (countryError) {
        errors.country = countryError;
        setTouchedFields(prev => ({ ...prev, country: true }));
      }
      if (!individualGuestFormData.dob || !String(individualGuestFormData.dob).trim()) {
        errors.dob = 'Date of birth is required.';
        setTouchedFields((prev) => ({ ...prev, dob: true }));
      } else if (moment(individualGuestFormData.dob).isAfter(moment(), 'day')) {
        errors.dob = 'Date of birth cannot be in the future.';
        setTouchedFields((prev) => ({ ...prev, dob: true }));
      }
      if (!individualGuestFormData.gender || !String(individualGuestFormData.gender).trim()) {
        errors.gender = 'Gender is required.';
        setTouchedFields((prev) => ({ ...prev, gender: true }));
      }
      if (isForeignGuest && !foreignGuestComplete) {
        errors.foreignGuest = 'Foreign guest details are required';
        setForeignGuestDrawerOpen(true);
      }
      // ID number + images required when a document type is selected — not for No Document.
      if (!isNoDocumentSelected) {
        const idNumberError = validateDocumentIdNumber(
          effectiveDocumentType,
          individualGuestFormData.id_number
        );
        if (idNumberError) {
          errors.id_number = idNumberError;
          setTouchedFields((prev) => ({ ...prev, id_number: true, document_type: true }));
        }
        const imageErrors = validateGuestDocumentImages(
          individualGuestFormData.document_type,
          individualGuestFormData.id_front,
          individualGuestFormData.id_back,
        );
        if (imageErrors.id_front) {
          errors.id_front = imageErrors.id_front;
          setTouchedFields((prev) => ({ ...prev, id_front: true }));
        }
        if (imageErrors.id_back) {
          errors.id_back = imageErrors.id_back;
          setTouchedFields((prev) => ({ ...prev, id_back: true }));
        }
      }
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setGuestFormError('Please fix the validation errors before saving.');
        setSavingGuest(false);
        return;
      }
      if (!firstName || !firstName.trim()) {
        setGuestFormError('Name is required.');
        setSavingGuest(false);
        return;
      }
      let resolvedGuestId = editingGuestId;
      if (!resolvedGuestId && guestSearchOptions.length > 0) {
        const digits = String(phoneNumber || '').replace(/\D/g, '');
        const matched =
          guestSearchOptions.find(
            (opt) => String(opt.phone || '').replace(/\D/g, '') === digits,
          ) || guestSearchOptions[0];
        if (matched?.id) {
          resolvedGuestId = matched.id;
          setEditingGuestId(matched.id);
        }
      }
      const guestData = {};
      if (resolvedGuestId) {
        guestData.id = resolvedGuestId;
      }
      if (stayGuestReplaceId) {
        guestData.replace_guest_id = stayGuestReplaceId;
      }
      if (!resolvedGuestId && !phoneNumber) {
        setGuestFormError('Mobile number is required for new guests.');
        setSavingGuest(false);
        return;
      }
      if (firstName && firstName.trim()) {
        guestData.first_name = firstName.trim();
      }
      if (middleName && middleName.trim()) {
        guestData.middle_name = middleName.trim();
      }
      if (lastName && lastName.trim()) {
        guestData.last_name = lastName.trim();
      }
      if (individualGuestFormData.email && individualGuestFormData.email.trim()) {
        guestData.email = individualGuestFormData.email.trim();
      }
      // Always send phone/country_code when editing by guest id so empty→filled updates apply.
      if (resolvedGuestId) {
        guestData.phone = phoneNumber ? phoneNumber.trim() : '';
        guestData.country_code = (countryCode || individualGuestFormData.phone_country_code || '+91').trim();
      } else if (phoneNumber && phoneNumber.trim()) {
        guestData.phone = phoneNumber.trim();
        if (countryCode && countryCode.trim()) {
          guestData.country_code = countryCode.trim();
        }
      } else if (countryCode && countryCode.trim()) {
        guestData.country_code = countryCode.trim();
      }
      if (individualGuestFormData.title && individualGuestFormData.title.trim()) {
        guestData.title = individualGuestFormData.title.trim();
      }
      if (individualGuestFormData.address && individualGuestFormData.address.trim()) {
        guestData.address = individualGuestFormData.address.trim();
      }
      if (individualGuestFormData.city && individualGuestFormData.city.trim()) {
        guestData.city = individualGuestFormData.city.trim();
      }
      if (individualGuestFormData.state && individualGuestFormData.state.trim()) {
        guestData.state = individualGuestFormData.state.trim();
      }
      if (individualGuestFormData.country && individualGuestFormData.country.trim()) {
        guestData.country = individualGuestFormData.country.trim();
      }
      if (individualGuestFormData.zipcode && individualGuestFormData.zipcode.trim()) {
        guestData.pincode = individualGuestFormData.zipcode.trim();
      }
      guestData.dob = individualGuestFormData.dob
        ? String(individualGuestFormData.dob).slice(0, 10)
        : '';
      if (individualGuestFormData.gender && individualGuestFormData.gender.trim()) {
        guestData.gender = individualGuestFormData.gender.trim();
      }
      if (isNoDocumentSelected) {
        guestData.document_type = '';
      } else if (effectiveDocumentType) {
        guestData.document_type = effectiveDocumentType;
      }
      if (effectiveDocumentType && individualGuestFormData.id_number && individualGuestFormData.id_number.trim()) {
        guestData.id_number = normalizeDocumentIdForSubmit(
          effectiveDocumentType,
          individualGuestFormData.id_number
        );
      }
      if (individualGuestFormData.id_front && typeof individualGuestFormData.id_front === 'string' && individualGuestFormData.id_front.startsWith('data:image')) {
        guestData.id_front = individualGuestFormData.id_front;
      }
      if (individualGuestFormData.id_back && typeof individualGuestFormData.id_back === 'string' && individualGuestFormData.id_back.startsWith('data:image')) {
        guestData.id_back = individualGuestFormData.id_back;
      }
      if (isForeignGuest) {
        Object.assign(guestData, foreignDetailsToGuestApiPayload(foreignGuestDetails));
      }
      const firstItemId = itemsToUpdate[0].id;
      const response = await api.patch(`/booking/api/${bookingId}/`, {
        booking_item_id: firstItemId,
        guest: guestData,
      });
      if (response.data.success) {
        setGuestFormDrawerOpen(false);
        setEditingGuestItemId(null);
        setEditingGuestId(null);
        setStayGuestReplaceId(null);
        setIsStayGuestEdit(false);
        setIsPhoneLocked(false);
        setValidationErrors({});
        setTouchedFields({});
        await fetchBookingDetails();
        markBookingModified();
      } else {
        setGuestFormError(formatGuestSaveError(response.data.error || 'Failed to update guest information.'));
      }
    } catch (err) {
      setGuestFormError(formatGuestSaveError(err.response?.data?.error || err.message || 'Failed to update guest information.'));
    } finally {
      setSavingGuest(false);
    }
  };

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (phoneSearchDebounceTimer) {
        clearTimeout(phoneSearchDebounceTimer);
      }
    };
  }, [stream, phoneSearchDebounceTimer]);

  const round2 = (x) => Math.round(Number(x) * 100) / 100;
  const formatCurrency = (amount, currency = 'INR') => {
    if (amount == null || amount === '' || Number(amount) === 0) {
      return `${getBookingCurrencySymbol(currency)}0.00`;
    }
    const value = round2(amount);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };
  const bookingCurrencySymbol = getBookingCurrencySymbol(booking?.currency || 'INR');

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('DD MMM YYYY');
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return null;
    return moment(dateTime).format('hh:mm A');
  };

  // Stay dates from booking; actual check-in/out time from backend display (hotel TZ).
  const getCheckInDisplay = () => resolveStayCheckInDisplay(booking);
  const getCheckOutDisplay = () => resolveStayCheckOutDisplay(booking);
  const actualCheckInDisplay = useMemo(() => resolveActualCheckInDisplay(booking), [booking]);
  const actualCheckOutDisplay = useMemo(() => resolveActualCheckOutDisplay(booking), [booking]);

  // Prefer backend early_checkin/early_checkout (hotel TZ); else compute client-side
  const getCheckinStatus = () => {
    if (booking?.early_checkin && typeof booking.early_checkin.hours === 'number') {
      const e = booking.early_checkin;
      if (e.is_early) return { type: 'early', hours: String(e.hours) };
      if (e.hours > 0) return { type: 'late', hours: String(e.hours) };
      return { type: 'on-time', hours: 0 };
    }
    const checkinTime = booking?.check_in_time || booking?.actual_checkin_time;
    if (!booking?.check_in_date || !checkinTime) return null;
    const checkinDate = moment(booking.check_in_date);
    const actualCheckinTime = moment(checkinTime);
    const expectedCheckin = checkinDate.clone().hour(14).minute(0).second(0);
    const diffHours = actualCheckinTime.diff(expectedCheckin, 'hours', true);
    if (diffHours < 0) return { type: 'early', hours: Math.abs(diffHours).toFixed(1) };
    if (diffHours > 0) return { type: 'late', hours: diffHours.toFixed(1) };
    return { type: 'on-time', hours: 0 };
  };

  const getCheckoutStatus = () => {
    if (booking?.early_checkout && typeof booking.early_checkout.hours === 'number') {
      const e = booking.early_checkout;
      if (e.is_early) return { type: 'early', hours: String(e.hours) };
      if (e.hours > 0) return { type: 'late', hours: String(e.hours) };
      return { type: 'on-time', hours: 0 };
    }
    const checkoutTime = booking?.check_out_time || booking?.actual_checkout_time;
    if (!booking?.check_out_date || !checkoutTime) return null;
    const checkoutDate = moment(booking.check_out_date);
    const actualCheckoutTime = moment(checkoutTime);
    const expectedCheckout = checkoutDate.clone().hour(11).minute(0).second(0);
    const diffHours = actualCheckoutTime.diff(expectedCheckout, 'hours', true);
    if (diffHours > 0) return { type: 'late', hours: diffHours.toFixed(1) };
    if (diffHours < 0) return { type: 'early', hours: Math.abs(diffHours).toFixed(1) };
    return { type: 'on-time', hours: 0 };
  };

  // Get unique room types with images and room numbers
  const getRoomDetails = () => {
    if (!booking?.items) return [];
    const roomMap = new Map();
    
    booking.items.forEach(item => {
      if (item.room_type) {
        const key = item.room_type.id;
        if (!roomMap.has(key)) {
          roomMap.set(key, {
            room_type: item.room_type,
            room_numbers: new Set(),
          });
        }
        if (item.allocated_room?.number) {
          roomMap.get(key).room_numbers.add(item.allocated_room.number);
        }
      }
    });
    
    return Array.from(roomMap.values()).map(room => ({
      ...room,
      room_numbers: Array.from(room.room_numbers).sort(),
    }));
  };


  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'confirmed') return 'primary';
    if (statusLower === 'checked_in') return 'success';
    if (statusLower === 'checked_out') return 'info';
    if (statusLower === 'cancelled') return 'error';
    if (['blocked', 'reserved', 'block'].includes(statusLower)) return 'warning';
    return 'default';
  };

  const bookingStatusLower = String(booking?.status || '').toLowerCase();
  const HeaderIcon = CalendarIcon;
  const headerTitle = 'Booking Details';

  // Streams still needing check-in (allocated or not): checked_in=false and not checked out.
  const hasRoomsPendingCheckIn = useMemo(() => {
    const items = booking?.items || booking?.booking_items || [];
    if (!items.length) return false;
    const streamFullyCheckedIn = new Map();
    items.forEach((item) => {
      const roomTypeId = item?.room_type_id ?? item?.room_type?.id;
      if (roomTypeId == null) return;
      const checkoutRaw = item?.checkout;
      const isCheckedOut = typeof checkoutRaw === 'string'
        ? ['1', 'true', 'yes'].includes(checkoutRaw.trim().toLowerCase())
        : checkoutRaw === true || checkoutRaw === 1;
      if (isCheckedOut) return;
      const checkedInRaw = item?.checked_in;
      const isCheckedIn = typeof checkedInRaw === 'string'
        ? ['1', 'true', 'yes'].includes(checkedInRaw.trim().toLowerCase())
        : checkedInRaw === true || checkedInRaw === 1;
      const key = `${roomTypeId}-${item.room_sequence ?? 1}`;
      const prev = streamFullyCheckedIn.get(key);
      streamFullyCheckedIn.set(key, prev === undefined ? isCheckedIn : prev && isCheckedIn);
    });
    for (const fullyCheckedIn of streamFullyCheckedIn.values()) {
      if (!fullyCheckedIn) return true;
    }
    return false;
  }, [booking?.items, booking?.booking_items]);

  // Open CheckInDrawer to check in Booked rooms / reallocate (same as check-in page flow).
  const canContinueCheckIn = useMemo(() => {
    if (!booking) return false;
    if (['cancelled', 'canceled', 'no_show', 'checked_out'].includes(bookingStatusLower)) {
      return false;
    }
    if (['confirmed', 'pending'].includes(bookingStatusLower)) {
      return true;
    }
    if (['partial_checkin', 'partial_checkout', 'checked_in'].includes(bookingStatusLower)) {
      return hasRoomsPendingCheckIn;
    }
    return false;
  }, [booking, bookingStatusLower, hasRoomsPendingCheckIn]);

  const safeBookingIdForCheckIn = normalizeBookingId(bookingId) || normalizeBookingId(booking?.id);

  const handleOpenContinueCheckIn = useCallback(() => {
    if (!safeBookingIdForCheckIn) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    setCheckInDrawerOpen(true);
  }, [safeBookingIdForCheckIn]);

  const handleCheckInDrawerClose = useCallback(() => {
    setCheckInDrawerOpen(false);
  }, []);

  const handleCheckInSuccessFromDetails = useCallback(async (updatedBooking) => {
    setCheckInDrawerOpen(false);
    markBookingModified();
    const id = normalizeBookingId(updatedBooking?.id) || safeBookingIdForCheckIn;
    if (id) {
      await fetchBookingDetails(id);
      onStatusUpdate?.(id);
    }
    notifyBookingMutation({ type: 'checkin', bookingId: id });
  }, [markBookingModified, normalizeBookingId, safeBookingIdForCheckIn, onStatusUpdate]);

  const handleModifyBookingFromCheckIn = useCallback((id) => {
    if (!canEditBooking) return;
    setCheckInDrawerOpen(false);
    const safeId = normalizeBookingId(id) || safeBookingIdForCheckIn;
    if (!safeId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    handleDrawerClose();
    navigate(`/booking/edit/${safeId}/`);
  }, [canEditBooking, normalizeBookingId, safeBookingIdForCheckIn, handleDrawerClose, navigate]);

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Header stays as booking details; check-in uses its own dedicated drawer. */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            p: 1.5,
            borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <BookingDrawerHeaderIdentity
            icon={<HeaderIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24, flexShrink: 0 }} />}
            title={headerTitle}
            booking={booking}
            bookingId={bookingId}
            onNavigate={handleDrawerClose}
            isDark={isDark}
            drawerTheme={drawerTheme}
            label={booking?.code || (booking?.id ? `#${booking.id}` : undefined)}
          />
          {canViewBookingAudit && (
            <Tooltip title="Open Booking Audit">
              <Button
                variant="outlined"
                size="small"
                startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
                onClick={() => setAuditDrawerOpen(true)}
                sx={{
                  borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
                  color: CUSTOM_BORDER_COLOR,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '999px',
                  px: 1.5,
                  py: 0.3,
                  minWidth: 84,
                  flexShrink: 0,
                  '&:hover': {
                    borderColor: CUSTOM_BORDER_COLOR,
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                  },
                }}
              >
                Audit
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Content - Scrollable with padding for fixed footer */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', p: 2, pb: 2 }}>
          {booking && (
            <>
              {/* Booking Information - Matching CheckInDrawer Design */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                  borderRadius: '12px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Booking Source Image and Status Chip - Top Right */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mb: 0,
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Booking Source Image - Channel or PMS */}
                  {booking.channel ? (
                    <ChannelImage channel={booking.channel} size={24} />
                  ) : (
                    <ChannelImage isPMS={true} size={24} />
                  )}
                  
                  {/* Booking Status Chip */}
                  {booking.status && (
                    <Chip
                      label={booking.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return isDark ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)';
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return isDark ? 'rgba(76, 175, 80, 0.22)' : 'rgba(76, 175, 80, 0.14)';
                          } else if (statusLower === 'checked_out') {
                            return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)';
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return isDark ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)';
                          }
                          return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                        })(),
                        color: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return isDark ? '#64b5f6' : '#1976d2';
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return isDark ? '#a5d6a7' : '#2e7d32';
                          } else if (statusLower === 'checked_out') {
                            return isDark ? '#bdbdbd' : '#616161';
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return isDark ? '#ef5350' : '#c62828';
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return isDark ? '#ffb74d' : '#e65100';
                          }
                          return isDark ? '#bdbdbd' : '#616161';
                        })(),
                        border: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return `1px solid ${isDark ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.25)'}`;
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return `1px solid ${isDark ? 'rgba(76, 175, 80, 0.55)' : 'rgba(76, 175, 80, 0.4)'}`;
                          } else if (statusLower === 'checked_out') {
                            return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return `1px solid ${isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'}`;
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return `1px solid ${isDark ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'}`;
                          }
                          return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                        })(),
                        backdropFilter: 'blur(10px)',
                        boxShadow: isDark 
                          ? '0 2px 8px rgba(0,0,0,0.3)' 
                          : '0 2px 8px rgba(0,0,0,0.1)',
                        textTransform: 'capitalize',
                        letterSpacing: '0.5px',
                      }}
                    />
                  )}
                  {isSameDayStay(booking) && (
                    <Chip
                      label="Hourly"
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: isDark ? 'rgba(255, 167, 38, 0.2)' : 'rgba(255, 167, 38, 0.12)',
                        color: isDark ? '#ffb74d' : '#ef6c00',
                        border: `1px solid ${isDark ? 'rgba(255, 183, 77, 0.45)' : 'rgba(239, 108, 0, 0.35)'}`,
                      }}
                    />
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  {/* Primary Guest Information */}
                  {(() => {
                    // Get primary guest information
                    const primaryGuest = booking.guest || null;
                    const guestName = booking.guest_name || 
                      (primaryGuest ? [primaryGuest.title, primaryGuest.first_name, primaryGuest.middle_name, primaryGuest.last_name].filter(Boolean).join(' ') : null) ||
                      (primaryGuest ? primaryGuest.name : null);
                    const guestPhone = booking.guest_phone || booking.phone || 
                      (primaryGuest ? primaryGuest.phone : null);
                    const guestEmail = primaryGuest ? primaryGuest.email : null;
                    
                    if (!guestName && !guestPhone) return null;
                    
                    return (
                      <>
                        {/* Guest Name */}
                        {guestName && (
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                                <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                                  Guest Name:
                        </Typography>
                                <DrawerGuestFieldLink
                                  isDark={isDark}
                                  booking={booking}
                                  guestId={booking?.guest_id ?? primaryGuest?.id}
                                  variant="body2"
                                  onNavigate={handleDrawerClose}
                                  sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                                >
                                  {guestName}
                                </DrawerGuestFieldLink>
                      </Box>
                    </Box>
                  </Grid>
                        )}

                        {/* Mobile Number */}
                        {guestPhone && (
                    <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                              <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                                Mobile Number:
                        </Typography>
                              <DrawerGuestFieldLink
                                isDark={isDark}
                                booking={booking}
                                guestId={booking?.guest_id ?? primaryGuest?.id}
                                variant="body2"
                                onNavigate={handleDrawerClose}
                                sx={{ fontWeight: 500, fontSize: '0.85rem' }}
                              >
                                {guestPhone}
                              </DrawerGuestFieldLink>
                      </Box>
                      </Grid>
                    )}

                        {/* Email */}
                        {guestEmail && (
                      <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                              <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                                Email:
                              </Typography>
                              <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.85rem' }}>
                                {guestEmail}
                          </Typography>
                    </Box>
                  </Grid>
                    )}
                      </>
                    );
                  })()}

                  {/* Check-in Information */}
                  <Grid item xs={12} sm={6}>
                    <Box
                sx={{
                        p: 1,
                        borderRadius: '8px',
                        background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        minHeight: booking.check_in_time ? 'auto' : '72px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckInIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                            Check-In
                  </Typography>
                </Box>
                        {(() => {
                          const checkinStatus = getCheckinStatus();
                          if (checkinStatus && checkinStatus.type !== 'on-time') {
                            return (
                              <Chip
                                label={checkinStatus.type === 'early' 
                                  ? `Early ${checkinStatus.hours}h` 
                                  : `Late ${checkinStatus.hours}h`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.6rem',
                                  backgroundColor: checkinStatus.type === 'early'
                                    ? (isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)')
                                    : (isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)'),
                                  color: checkinStatus.type === 'early'
                                    ? (isDark ? '#81c784' : '#2e7d32')
                                    : (isDark ? '#ffb74d' : '#f57c00'),
                                  fontWeight: 600,
                                  border: `1px solid ${checkinStatus.type === 'early'
                                    ? (isDark ? '#81c784' : '#2e7d32')
                                    : (isDark ? '#ffb74d' : '#f57c00')}60`,
                                }}
                              />
                            );
                          }
                          return null;
                        })()}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                        {(getCheckInDisplay()?.date) || formatDate(booking.check_in_date)}
                      </Typography>
                      {(getCheckInDisplay()?.time) || (booking.check_in_time || booking.actual_checkin_time) ? (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                          {getCheckInDisplay()?.time || formatTime(booking.check_in_time || booking.actual_checkin_time)}
                          </Typography>
                      ) : (
                        <Box sx={{ height: '16px' }} />
                      )}
                        </Box>
                    <ActualStayDateChips actual={actualCheckInDisplay} label="Actual check-in" isDark={isDark} />
                      </Grid>

                  {/* Check-out Information */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '8px',
                        background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        minHeight: booking.check_out_time ? 'auto' : '72px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckOutIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                            Check-Out
                          </Typography>
                        </Box>
                        {(() => {
                          const checkoutStatus = getCheckoutStatus();
                          if (checkoutStatus && checkoutStatus.type !== 'on-time') {
                            return (
                              <Chip
                                label={checkoutStatus.type === 'early' 
                                  ? `Early ${checkoutStatus.hours}h` 
                                  : `Late ${checkoutStatus.hours}h`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.6rem',
                                  backgroundColor: checkoutStatus.type === 'early'
                                    ? (isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)')
                                    : (isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)'),
                                  color: checkoutStatus.type === 'early'
                                    ? (isDark ? '#81c784' : '#2e7d32')
                                    : (isDark ? '#ffb74d' : '#f57c00'),
                                  fontWeight: 600,
                                  border: `1px solid ${checkoutStatus.type === 'early'
                                    ? (isDark ? '#81c784' : '#2e7d32')
                                    : (isDark ? '#ffb74d' : '#f57c00')}60`,
                                }}
                              />
                            );
                          }
                          return null;
                        })()}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                        {(getCheckOutDisplay()?.date) || formatDate(booking.check_out_date)}
                      </Typography>
                      {(getCheckOutDisplay()?.time) || (booking.check_out_time || booking.actual_checkout_time) ? (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                          {getCheckOutDisplay()?.time || formatTime(booking.check_out_time || booking.actual_checkout_time)}
                        </Typography>
                      ) : (
                        <Box sx={{ height: '16px' }} />
                      )}
                    </Box>
                    <ActualStayDateChips actual={actualCheckOutDisplay} label="Actual check-out" isDark={isDark} />
                  </Grid>

                  {/* Nights / Hourly and Rooms */}
                        <Grid item xs={6}>
                    <StayDurationChip
                      booking={booking}
                      isDark={isDark}
                      accentColor={CUSTOM_BORDER_COLOR}
                      captionSx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
                    />
                      </Grid>
                        <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                        Number of Rooms:
                          </Typography>
                      {(() => {
                        const detailItems = booking.items || booking.booking_items || [];
                        const streamKeys = new Set();
                        const allocatedStreamKeys = new Set();
                        detailItems.forEach((item) => {
                          if (item?.checkout === true) return;
                          const rtId = item.room_type?.id || item.room_type_id;
                          if (!rtId) return;
                          const key = `${rtId}_${item.room_sequence || 1}`;
                          streamKeys.add(key);
                          const roomId = item.allocated_room?.id || item.allocated_room_id;
                          if (roomId) allocatedStreamKeys.add(key);
                        });
                        const totalRooms = Math.max(
                          Number(booking.total_rooms || booking.rooms_count || booking.rooms || 0) || 0,
                          streamKeys.size,
                        );
                        const allocatedRooms = allocatedStreamKeys.size;
                        const showPartial = totalRooms > 0 && allocatedRooms > 0 && allocatedRooms < totalRooms;
                        return (
                          <Tooltip
                            title={showPartial
                              ? `${allocatedRooms} of ${totalRooms} rooms allocated. Allocate the rest at check-in.`
                              : ''}
                            arrow
                            disableHoverListener={!showPartial}
                          >
                            <Chip
                              icon={<HotelIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />}
                              label={showPartial ? `${allocatedRooms} / ${totalRooms}` : totalRooms}
                              size="small"
                              sx={{
                                backgroundColor: showPartial
                                  ? (isDark ? 'rgba(255, 152, 0, 0.18)' : 'rgba(255, 152, 0, 0.12)')
                                  : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                                color: showPartial ? '#ff9800' : CUSTOM_BORDER_COLOR,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                                border: `1px solid ${showPartial ? 'rgba(255, 152, 0, 0.45)' : `${CUSTOM_BORDER_COLOR}40`}`,
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                        </Box>
                      </Grid>
                      {String(booking.special_requests || '').trim() && (
                        <Grid item xs={12}>
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.25,
                              color: isDark ? '#9ca3af' : '#666',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              letterSpacing: 0.3,
                              textTransform: 'uppercase',
                              display: 'block',
                            }}
                          >
                            Special requests
                          </Typography>
                          <Box
                            sx={{
                              mt: 0.25,
                              px: 0.85,
                              py: 0.6,
                              borderRadius: 0.85,
                                borderLeft: '2px solid #f59e0b',
                              backgroundColor: isDark
                                  ? 'rgba(250, 204, 21, 0.22)'
                                  : 'rgba(254, 240, 138, 0.55)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDark ? drawerTheme.text : '#333',
                                fontSize: '0.76rem',
                                lineHeight: 1.4,
                                whiteSpace: 'pre-line',
                                fontStyle: 'italic',
                              }}
                            >
                              {String(booking.special_requests || '').trim()}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                  </Grid>
              </Paper>

              {/* Room Cards - Matching CheckInDrawer Design */}
              {(() => {
                const bookingItems = booking.items || booking.booking_items || [];
                if (!bookingItems || bookingItems.length === 0) return null;

                // Detect sequences where allocated room changed across dates
                const sequenceToRooms = new Map();
                bookingItems.forEach((item) => {
                  const roomTypeId = item.room_type?.id || item.room_type_id;
                  if (!roomTypeId) return;
                  const seq = item.room_sequence || 1;
                  const key = `${roomTypeId}_${seq}`;
                  const roomId = item.allocated_room?.id || item.allocated_room_id || null;
                  if (!roomId) return;
                  if (!sequenceToRooms.has(key)) sequenceToRooms.set(key, new Set());
                  sequenceToRooms.get(key).add(Number(roomId));
                });
                const changedSequenceKeys = new Set();
                sequenceToRooms.forEach((roomIds, key) => {
                  if (roomIds.size > 1) changedSequenceKeys.add(key);
                });

                // Detect upgraded room-types by sequence/date:
                // show only on the changed/new room-type, not every room in the sequence.
                const sequenceRoomTypeStartDate = new Map();
                const sequenceItems = new Map();
                const explicitUpgradeCandidates = new Set();
                const getItemDateKey = (item) => {
                  if (!item?.date) return '';
                  return moment.isMoment(item.date) ? item.date.format('YYYY-MM-DD') : String(item.date).slice(0, 10);
                };
                bookingItems.forEach((item) => {
                  const roomTypeId = Number(item.room_type?.id || item.room_type_id || 0);
                  if (!roomTypeId) return;
                  const seq = Number(item.room_sequence || 1);
                  if (!sequenceItems.has(seq)) sequenceItems.set(seq, []);
                  sequenceItems.get(seq).push(item);
                  const upgradeFromTypeId = Number(
                    (item.upgraded_from_room_type_id ?? item.upgraded_from_room_type?.id) || 0
                  );
                  if (upgradeFromTypeId && upgradeFromTypeId !== roomTypeId) {
                    explicitUpgradeCandidates.add(`${seq}_${roomTypeId}`);
                  }
                  const itemDate = getItemDateKey(item);
                  if (!itemDate) return;
                  if (!sequenceRoomTypeStartDate.has(seq)) sequenceRoomTypeStartDate.set(seq, new Map());
                  const perType = sequenceRoomTypeStartDate.get(seq);
                  const currentStart = perType.get(roomTypeId);
                  if (!currentStart || itemDate < currentStart) {
                    perType.set(roomTypeId, itemDate);
                  }
                });
                // Always trust explicit lineage markers from backend payload.
                // Fallback heuristics below only add missing candidates when marker is absent.
                const upgradedRoomTypeKeys = new Set(explicitUpgradeCandidates);
                sequenceRoomTypeStartDate.forEach((perType, seq) => {
                  const dates = [...perType.values()].filter(Boolean);
                  if (dates.length <= 1) return;
                  const sequenceStartDate = dates.reduce((minDate, d) => (d < minDate ? d : minDate), dates[0]);
                  const seqItems = sequenceItems.get(seq) || [];
                  perType.forEach((typeStartDate, rtId) => {
                    // Mark fallback upgrade only for real mid-stay upgrades with prior checked-out history.
                    const hasPriorCheckedOutHistory = seqItems.some((it) => {
                      const itTypeId = Number(it.room_type?.id || it.room_type_id || 0);
                      if (!itTypeId || itTypeId === rtId) return false;
                      const itDate = getItemDateKey(it);
                      if (!itDate || itDate >= typeStartDate) return false;
                      return it.checkout === true;
                    });
                    const key = `${seq}_${rtId}`;
                    const isExplicitCandidate = explicitUpgradeCandidates.has(key);
                    if (isExplicitCandidate) {
                      upgradedRoomTypeKeys.add(key);
                    } else if (typeStartDate > sequenceStartDate && hasPriorCheckedOutHistory) {
                      upgradedRoomTypeKeys.add(key);
                    }
                  });
                });
                const roomTypeSortOrderById = buildRoomTypeSortOrderMap(bookingItems);
                
                // Group allocated items by physical room (room changes keep separate cards)
                const roomsMap = new Map();
                const allocatedStreamKeys = new Set();
                bookingItems.forEach(item => {
                  const roomId = item.allocated_room?.id || item.allocated_room_id;
                  const roomNumber = item.allocated_room?.number || item.allocated_room_number;
                  if (roomId && roomNumber) {
                    const rtId = item.room_type?.id || item.room_type_id;
                    if (rtId && item.checkout !== true) {
                      allocatedStreamKeys.add(`${rtId}_${item.room_sequence || 1}`);
                    }
                    if (!roomsMap.has(roomId)) {
                      roomsMap.set(roomId, {
                        roomId,
                        roomNumber,
                        isUnallocated: false,
                        roomType: item.room_type,
                        items: [],
                        guests: [],
                      });
                    }
                    const room = roomsMap.get(roomId);
                    room.items.push(item);
                    if (item.guests && Array.isArray(item.guests)) {
                      item.guests.forEach(guest => {
                        if (!room.guests.find(g => g.id === guest.id)) {
                          room.guests.push(guest);
                        }
                      });
                    }
                  }
                });

                // Show booked streams that still need a physical room (matches Number of Rooms)
                const unallocatedStreams = new Map();
                bookingItems.forEach((item) => {
                  if (item?.checkout === true) return;
                  const rtId = item.room_type?.id || item.room_type_id;
                  if (!rtId) return;
                  const seq = item.room_sequence || 1;
                  const streamKey = `${rtId}_${seq}`;
                  if (allocatedStreamKeys.has(streamKey)) return;
                  const roomId = item.allocated_room?.id || item.allocated_room_id;
                  if (roomId) return;
                  if (!unallocatedStreams.has(streamKey)) {
                    unallocatedStreams.set(streamKey, {
                      roomId: `unallocated-${streamKey}`,
                      roomNumber: 'Unallocated',
                      isUnallocated: true,
                      roomType: item.room_type || {
                        id: rtId,
                        name: item.room_type_name || item.room_type?.name || 'Room',
                      },
                      items: [],
                      guests: [],
                    });
                  }
                  const room = unallocatedStreams.get(streamKey);
                  room.items.push(item);
                  if (item.guests && Array.isArray(item.guests)) {
                    item.guests.forEach((guest) => {
                      if (!room.guests.find((g) => g.id === guest.id)) {
                        room.guests.push(guest);
                      }
                    });
                  }
                });
                
                const rooms = [...roomsMap.values(), ...unallocatedStreams.values()];
                if (rooms.length === 0) return null;
                // Show checked-out rooms last
                const sortedRooms = [...rooms].sort((a, b) => {
                  const aCheckedOut = a.items?.every((item) => item.checkout === true) ? 1 : 0;
                  const bCheckedOut = b.items?.every((item) => item.checkout === true) ? 1 : 0;
                  return aCheckedOut - bCheckedOut;
                });

                const roomDateBounds = (room) => {
                  const keys = (room.items || [])
                    .map((item) => {
                      if (!item?.date) return '';
                      return moment.isMoment(item.date)
                        ? item.date.format('YYYY-MM-DD')
                        : String(item.date).slice(0, 10);
                    })
                    .filter(Boolean)
                    .sort();
                  return { first: keys[0] || '', last: keys[keys.length - 1] || '' };
                };
                let arrivalRoomId = sortedRooms[0]?.roomId ?? null;
                let departingRoomId = sortedRooms[sortedRooms.length - 1]?.roomId ?? null;
                {
                  let earliest = null;
                  let latest = null;
                  sortedRooms.forEach((room) => {
                    const { first, last } = roomDateBounds(room);
                    if (first && (!earliest || first < earliest.first)) {
                      earliest = { id: room.roomId, first };
                    }
                    if (last && (!latest || last > latest.last)) {
                      latest = { id: room.roomId, last };
                    }
                  });
                  arrivalRoomId = earliest?.id ?? arrivalRoomId;
                  departingRoomId = latest?.id ?? departingRoomId;
                }
                
                return (
                  <Box sx={{ mb: 1.5, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ color: isDark ? drawerTheme.text : '#333', mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
                      Rooms
                    </Typography>
                    <Grid container spacing={1.5}>
                      {sortedRooms.map((room) => {
                        const roomTypeId = room.roomType?.id;
                        const imageUrl = roomTypeId ? roomTypeImages[roomTypeId] : null;
                        const firstItem = room.items[0];
                        const roomItems = Array.isArray(room.items) ? room.items : [];
                        const roomDateKeys = new Set(
                          roomItems
                            .map((item) => {
                              if (!item?.date) return '';
                              return moment.isMoment(item.date)
                                ? item.date.format('YYYY-MM-DD')
                                : String(item.date).slice(0, 10);
                            })
                            .filter(Boolean)
                        );
                        const roomNights = roomDateKeys.size || roomItems.length || 0;
                        const roomStayMeta = resolveRoomSegmentStayMeta(booking, roomItems, {
                          isArrivalSegment: room.roomId === arrivalRoomId,
                          isDepartingSegment: room.roomId === departingRoomId,
                        });
                        const totalPrice = roomItems.reduce((sum, item) => {
                          const amount = Number(item?.price);
                          return sum + (Number.isFinite(amount) ? amount : 0);
                        }, 0);
                        const pricePerNight = roomNights > 0 ? totalPrice / roomNights : 0;
                        const itemAdults = Math.max(
                          0,
                          ...roomItems.map((item) => Number(item?.adults) || 0),
                        );
                        const itemChildren = Math.max(
                          0,
                          ...roomItems.map((item) => Number(item?.children) || 0),
                        );
                        const bookedAdults = itemAdults > 0
                          ? itemAdults
                          : (Number(firstItem?.adults) || Number(booking?.pax_adults) || 0);
                        const roomChildren = itemChildren > 0
                          ? itemChildren
                          : (Number(firstItem?.children) || Number(booking?.pax_children) || 0);
                        const guestCountForRoom = Array.isArray(room.guests) ? room.guests.length : 0;
                        // Show booked adults; backend only raises adults when named guests exceed that.
                        const roomAdults = bookedAdults;
                        const roomTypeMaxGuests = Math.max(
                          1,
                          Number(room.roomType?.max_guests)
                            || Number(firstItem?.room_type?.max_guests)
                            || (Number(firstItem?.room_type?.base_adults) || 0)
                              + (Number(firstItem?.room_type?.base_children) || 0)
                            || bookedAdults + roomChildren
                            || 4,
                        );
                        const maxGuestsForRoom = roomTypeMaxGuests;
                        const atGuestCapacity = guestCountForRoom >= maxGuestsForRoom;
                        const ratePlanName = firstItem?.rate_plan?.name || null;
                        const isItemCheckedOutFlag = (item) => {
                          const raw = item?.checkout;
                          if (typeof raw === 'string') {
                            return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
                          }
                          return raw === true || raw === 1;
                        };
                        const isItemCheckedInFlag = (item) => {
                          const raw = item?.checked_in;
                          if (typeof raw === 'string') {
                            return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
                          }
                          return raw === true || raw === 1;
                        };
                        // Per-room stay status from item.checked_in / checkout — not booking.status.
                        const isRoomCheckedOut = roomItems.length > 0 && roomItems.every(isItemCheckedOutFlag);
                        const isRoomCheckedIn = !isRoomCheckedOut && roomItems.some(
                          (item) => isItemCheckedInFlag(item) && !isItemCheckedOutFlag(item),
                        );
                        const isRoomBookedAllocated = !room.isUnallocated && !isRoomCheckedOut && !isRoomCheckedIn;
                        const hasRoomChangedInSequence = room.items.some((item) => {
                          const rtId = item.room_type?.id || item.room_type_id;
                          const key = `${rtId}_${item.room_sequence || 1}`;
                          return changedSequenceKeys.has(key);
                        });
                        const hasRoomTypeUpgradedInSequence = room.items.some((item) => {
                          const seq = item.room_sequence || 1;
                          const rtId = Number(item.room_type?.id || item.room_type_id || 0);
                          return rtId && upgradedRoomTypeKeys.has(`${seq}_${rtId}`);
                        });
                        const roomTypeChangeKind = hasRoomTypeUpgradedInSequence
                          ? resolveRoomTypeChangeKindFromItems(
                            room.items,
                            roomTypeSortOrderById,
                            bookingItems,
                          )
                          : 'upgrade';
                        const roomTypeChangeLabel = getRoomTypeChangeLabel(roomTypeChangeKind);
                        const isRoomTypeDowngrade = roomTypeChangeKind === 'downgrade';
                        
                        return (
                          <Grid item xs={12} key={room.roomId}>
                            <Card
                              elevation={3}
                              sx={{
                                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                                borderRadius: 3,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                width: '100%',
                                maxWidth: '100%',
                                cursor: (isRoomCheckedOut || room.isUnallocated) ? 'default' : 'pointer',
                                opacity: isRoomCheckedOut ? 0.7 : 1,
                                border: room.isUnallocated
                                  ? `1px dashed ${isDark ? 'rgba(255, 152, 0, 0.55)' : 'rgba(245, 124, 0, 0.5)'}`
                                  : isRoomCheckedOut
                                  ? `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.2)'}`
                                  : 'none',
                                '&:hover': (!isRoomCheckedOut && !room.isUnallocated) ? {
                                  transform: 'translateY(-2px)',
                                  boxShadow: isDark
                                    ? '0 12px 24px rgba(0,0,0,0.5)'
                                    : '0 12px 24px rgba(0,0,0,0.12)',
                                } : {},
                              }}
                            >
                              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', minHeight: 100, minWidth: 0 }}>
                                  {/* Room Type Image - Left Side */}
                                  <Box
                                    sx={{
                                      ...bookingDrawerRoomImageSizeSx,
                                      height: 'auto',
                                      position: 'relative',
                                      backgroundImage: imageUrl
                                        ? `linear-gradient(135deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 100%), url(${imageUrl})`
                                        : `linear-gradient(135deg, ${isDark ? '#3a3a4a' : '#e8e8e8'} 0%, ${isDark ? '#2a2a3a' : '#d8d8d8'} 100%)`,
                                      backgroundColor: isDark ? '#3a3a4a' : '#e8e8e8',
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      borderTopLeftRadius: 12,
                                      borderTopRightRadius: 0,
                                      borderBottomLeftRadius: 12,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {!imageUrl && (
                                      <RoomIcon sx={{ fontSize: 40, color: isDark ? drawerTheme.subtleText : '#999', opacity: 0.5 }} />
                                    )}
                                    
                                    {/* Room Number Badge on Image */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        left: 8,
                                        right: 8,
                                        zIndex: 1,
                                      }}
                                    >
                                      <Chip
                                        icon={<RoomIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                        label={room.roomNumber}
                                        size="small"
                                        sx={{
                                          backgroundColor: room.isUnallocated
                                            ? (isDark ? 'rgba(255, 152, 0, 0.88)' : 'rgba(255, 152, 0, 0.9)')
                                            : 'rgba(0,0,0,0.75)',
                                          color: '#fff',
                                          fontWeight: 700,
                                          fontSize: '0.75rem',
                                          height: 24,
                                          backdropFilter: 'blur(10px)',
                                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                          '& .MuiChip-icon': {
                                            color: '#fff !important',
                                            fontSize: 14,
                                          },
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                  
                                  {/* Guest Information - Right Side */}
                                  <Box sx={{ 
                                    flex: 1,
                                    minWidth: 0,
                                    p: 1.5, 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    justifyContent: 'space-between',
                                    background: isDark 
                                      ? drawerTheme.surfaceGradient
                                      : 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                                  }}>
                                    {/* Room Type, Rate Plan, and Pricing Information */}
                                    <Box sx={{ mb: 1.5 }}>
                                      {/* Room Type with Rate Plan */}
                                      <Box sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', width: '100%', justifyContent: 'space-between' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0, flex: '1 1 150px' }}>
                                            <HotelIcon sx={{ fontSize: 16, color: CUSTOM_BORDER_COLOR, flexShrink: 0 }} />
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                color: isDark ? '#fff' : '#1a1a1a',
                                                fontWeight: 700,
                                                fontSize: '0.85rem',
                                                letterSpacing: '0.01em',
                                                lineHeight: 1.2,
                                                minWidth: 0,
                                                maxWidth: '100%',
                                              }}
                                            >
                                              {room.roomType?.name || 'Room'}
                                            </Typography>
                                            {hasRoomChangedInSequence && (
                                              <Tooltip title="Room change" arrow>
                                                <RoomChangeIcon
                                                  sx={{
                                                    fontSize: 15,
                                                    color: '#ff9800',
                                                    flexShrink: 0,
                                                    opacity: 0.95,
                                                  }}
                                                />
                                              </Tooltip>
                                            )}
                                            {hasRoomTypeUpgradedInSequence && (
                                              <Tooltip title={roomTypeChangeLabel} arrow>
                                                <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                                                  {isRoomTypeDowngrade ? (
                                                    <RoomDowngradeIcon
                                                      sx={{
                                                        fontSize: 15,
                                                        color: '#f59e0b',
                                                        opacity: 0.95,
                                                      }}
                                                    />
                                                  ) : (
                                                    <RoomUpgradeIcon
                                                      sx={{
                                                        fontSize: 15,
                                                        color: '#b8860b',
                                                        opacity: 0.95,
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                              </Tooltip>
                                            )}
                                            {ratePlanName && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: isDark ? drawerTheme.subtleText : '#666',
                                                fontSize: '0.7rem',
                                                  fontStyle: 'italic',
                                                }}
                                              >
                                                ({ratePlanName})
                                              </Typography>
                                            )}
                                          </Box>
                                          {room.isUnallocated ? (
                                            <Chip
                                              label="Needs allocation"
                                              size="small"
                                              sx={{
                                                height: 20,
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                ml: 'auto',
                                                backgroundColor: isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.12)',
                                                color: '#ff9800',
                                                border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.45)' : 'rgba(255, 152, 0, 0.35)'}`,
                                              }}
                                            />
                                          ) : null}
                                          {/* Per-room stay status (checked_in / booked / checked out) */}
                                          {!room.isUnallocated && (isRoomCheckedOut || isRoomCheckedIn || isRoomBookedAllocated) && (
                                            <Chip
                                              icon={
                                                isRoomCheckedOut
                                                  ? <CheckOutIcon sx={{ fontSize: 12 }} />
                                                  : isRoomCheckedIn
                                                    ? <CheckInIcon sx={{ fontSize: 12 }} />
                                                    : undefined
                                              }
                                              label={
                                                isRoomCheckedOut
                                                  ? 'Checked Out'
                                                  : isRoomCheckedIn
                                                    ? 'Checked In'
                                                    : 'Booked'
                                              }
                                              size="small"
                                              sx={{
                                                height: 20,
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                ml: 'auto',
                                                maxWidth: '100%',
                                                backgroundColor: isRoomCheckedOut
                                                  ? (isDark ? 'rgba(33, 150, 243, 0.25)' : 'rgba(33, 150, 243, 0.15)')
                                                  : isRoomCheckedIn
                                                    ? (isDark ? 'rgba(76, 175, 80, 0.25)' : 'rgba(76, 175, 80, 0.15)')
                                                    : (isDark ? 'rgba(255, 167, 38, 0.2)' : 'rgba(255, 152, 0, 0.12)'),
                                                color: isRoomCheckedOut
                                                  ? (isDark ? '#90caf9' : '#1565c0')
                                                  : isRoomCheckedIn
                                                    ? (isDark ? '#81c784' : '#2e7d32')
                                                    : (isDark ? '#ffb74d' : '#ef6c00'),
                                                border: `1px solid ${
                                                  isRoomCheckedOut
                                                    ? (isDark ? 'rgba(33, 150, 243, 0.4)' : 'rgba(33, 150, 243, 0.3)')
                                                    : isRoomCheckedIn
                                                      ? (isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)')
                                                      : (isDark ? 'rgba(255, 167, 38, 0.45)' : 'rgba(255, 152, 0, 0.35)')
                                                }`,
                                                '& .MuiChip-icon': { color: 'inherit' },
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                      
                                      {/* Pricing Information Card */}
                <Paper
                  elevation={0}
                  sx={{
                                          p: 1,
                                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                                          borderRadius: 1.5,
                                          border: `1px solid ${CUSTOM_BORDER_COLOR}25`,
                                        }}
                                      >
                                        {/* Price Per Night + occupancy */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, minWidth: 0, gap: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                            <Typography
                                              component="span"
                                              sx={{
                                                color: CUSTOM_BORDER_COLOR,
                                                fontSize: '1rem',
                                                fontWeight: 800,
                                                lineHeight: 1,
                                              }}
                                            >
                                              {bookingCurrencySymbol}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: isDark ? drawerTheme.text : '#333',
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                              }}
                                            >
                                              {pricePerNight.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                              <PersonIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: isDark ? drawerTheme.text : '#333',
                                                  fontSize: '0.7rem',
                                                  fontWeight: 600,
                                                  lineHeight: 1,
                                                }}
                                              >
                                                {roomAdults}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                              <ChildCareIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: isDark ? drawerTheme.text : '#333',
                                                  fontSize: '0.7rem',
                                                  fontWeight: 600,
                                                  lineHeight: 1,
                                                }}
                                              >
                                                {roomChildren}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Box>
                                        
                                        {/* Rooms × Nights = Total */}
                                        <Box sx={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          justifyContent: 'space-between',
                                          gap: 0.75,
                                          minWidth: 0,
                                          pt: 0.75,
                                          borderTop: `1px solid ${CUSTOM_BORDER_COLOR}20`,
                                        }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexWrap: 'wrap' }}>
                                            {roomStayMeta ? (
                                              <>
                                                <Chip
                                                  label={roomStayMeta.bookedChipLabel}
                                                  size="small"
                                                  title="Booked nights in this room"
                                                  sx={{
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                    color: CUSTOM_BORDER_COLOR,
                                                    border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                                                  }}
                                                />
                                                <Chip
                                                  label={roomStayMeta.stayChipLabel}
                                                  size="small"
                                                  title={roomStayMeta.summaryText}
                                                  sx={{
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    backgroundColor: isDark ? 'rgba(255, 167, 38, 0.2)' : 'rgba(255, 167, 38, 0.12)',
                                                    color: isDark ? '#ffb74d' : '#ef6c00',
                                                    border: `1px solid ${isDark ? '#ffb74d60' : '#ef6c0040'}`,
                                                  }}
                                                />
                                              </>
                                            ) : (
                                              <>
                                                <HelpOutlineIcon sx={{ fontSize: 14, color: isDark ? drawerTheme.subtleText : '#666' }} />
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    color: isDark ? drawerTheme.subtleText : '#666',
                                                    fontSize: '0.75rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                  }}
                                                >
                                                  Room × {roomNights} {roomNights === 1 ? 'night' : 'nights'}
                                                </Typography>
                                              </>
                                            )}
                                          </Box>
                                          
                                          {/* Total Price */}
                                          <Chip
                                            label={`${bookingCurrencySymbol} ${totalPrice.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`}
                                            sx={{
                                              backgroundColor: CUSTOM_BORDER_COLOR,
                                              color: '#000',
                                              fontWeight: 700,
                                              fontSize: '0.8rem',
                                              height: 26,
                                              flexShrink: 0,
                                              boxShadow: `0 2px 6px ${CUSTOM_BORDER_COLOR}40`,
                                            }}
                                          />
                                        </Box>
                </Paper>
                                    </Box>
                                    
                                    {/* Guest Slider Section */}
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                      {room.guests.length > 0 ? (
                                        <Box sx={{ position: 'relative', minHeight: 50, py: 0.5 }}>
                                          {(() => {
                                            const currentSlideIndex = guestSlideIndex[room.roomId] || 0;
                                            // Ensure index is within bounds
                                            const safeIndex = Math.min(Math.max(0, currentSlideIndex), room.guests.length - 1);
                                            const currentGuest = room.guests[safeIndex];
                                            
                                            // Safety check: ensure currentGuest exists
                                            if (!currentGuest) {
                                              return (
                                                <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', textAlign: 'center' }}>
                                                  No guest data available
                                                </Typography>
                                              );
                                            }
                                            
                                            const guestName = currentGuest.name || `${currentGuest.first_name || ''} ${currentGuest.middle_name || ''} ${currentGuest.last_name || ''}`.trim() || 'Guest';
                                            const guestPhone = currentGuest.phone || '—';
                                            
                                            return (
                                              <Box
                                                sx={{
                                                  position: 'relative',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: 1,
                                                }}
                                              >
                                                {/* Previous Button */}
                                                {room.guests.length > 1 && (
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const prevIndex = safeIndex > 0 ? safeIndex - 1 : room.guests.length - 1;
                                                      setGuestSlideIndex(prev => ({ ...prev, [room.roomId]: prevIndex }));
                                                    }}
                                                    sx={{
                                                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                      color: CUSTOM_BORDER_COLOR,
                                                      border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                                                      width: 32,
                                                      height: 32,
                                                      '&:hover': {
                                                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.2)',
                                                        transform: 'scale(1.1)',
                                                      },
                                                      transition: 'all 0.2s ease',
                                                    }}
                                                  >
                                                    <ChevronLeftIcon sx={{ fontSize: 20 }} />
                                                  </IconButton>
                                                )}
                                                
                                                {/* Guest Info Card */}
              <Paper
                elevation={0}
                sx={{
                                                    flex: 1,
                                                    p: 1,
                                                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                                                    borderRadius: 1.5,
                                                    border: `1px solid ${CUSTOM_BORDER_COLOR}30`,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                                                    },
                                                  }}
                                                >
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {/* Avatar on Left */}
                                                    <Avatar
                                                      sx={{
                                                        width: 32,
                                                        height: 32,
                                                        bgcolor: CUSTOM_BORDER_COLOR,
                                                        color: '#000',
                                                        fontWeight: 700,
                                                        fontSize: '0.85rem',
                                                        flexShrink: 0,
                                                      }}
                                                    >
                                                      {guestName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    
                                                    {/* Name and Phone on Right */}
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                                        <DrawerGuestFieldLink
                                                          isDark={isDark}
                                                          guest={currentGuest}
                                                          guestId={currentGuest?.id}
                                                          booking={booking}
                                                          variant="body2"
                                                          onNavigate={handleDrawerClose}
                                                          sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            textOverflow: 'ellipsis',
                                                            overflow: 'hidden',
                                                            whiteSpace: 'nowrap',
                                                            display: 'block',
                                                            minWidth: 0,
                                                          }}
                                                        >
                                                          {guestName}
                                                        </DrawerGuestFieldLink>
                                                        {isGuestChild(currentGuest) && (
                                                          <Chip
                                                            icon={<ChildCareIcon sx={{ fontSize: '12px !important' }} />}
                                                            label="Child"
                                                            size="small"
                                                            sx={{
                                                              height: 18,
                                                              fontSize: '0.6rem',
                                                              fontWeight: 700,
                                                              flexShrink: 0,
                                                              bgcolor: isDark ? 'rgba(33, 150, 243, 0.22)' : 'rgba(33, 150, 243, 0.12)',
                                                              color: isDark ? '#90caf9' : '#1565c0',
                                                              border: `1px solid ${isDark ? 'rgba(144, 202, 249, 0.45)' : 'rgba(21, 101, 192, 0.3)'}`,
                                                              '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                                                            }}
                                                          />
                                                        )}
                                                        {isForeignGuestRecord(currentGuest, hotelCountry) && (
                                                          <Chip
                                                            icon={<PublicIcon sx={{ fontSize: '12px !important' }} />}
                                                            label="Foreign"
                                                            size="small"
                                                            sx={getForeignGuestChipSx(isDark)}
                                                          />
                                                        )}
                                                      </Box>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <PhoneIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                                                        <DrawerGuestFieldLink
                                                          isDark={isDark}
                                                          guest={currentGuest}
                                                          guestId={currentGuest?.id}
                                                          booking={booking}
                                                          variant="caption"
                                                          onNavigate={handleDrawerClose}
                                                          sx={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: 500,
                                                          }}
                                                        >
                                                          {guestPhone}
                                                        </DrawerGuestFieldLink>
                                                      </Box>
                                                      {room.guests.length > 1 && (
                                                        <Chip
                                                          label={`${safeIndex + 1} / ${room.guests.length}`}
                                                          size="small"
                                                          sx={{
                                                            mt: 0.5,
                                                            height: 18,
                                                            fontSize: '0.6rem',
                                                            bgcolor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                                                            color: CUSTOM_BORDER_COLOR,
                                                            fontWeight: 600,
                                                          }}
                                                        />
                                                      )}
                                                    </Box>
                                                    
                                                    {/* Action Icons */}
                                                    <Box
                                                      sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        gap: 0.5,
                                                        flexShrink: 0,
                                                        ml: 0.75,
                                                        '& .MuiIconButton-root': {
                                                          width: 32,
                                                          height: 32,
                                                        },
                                                      }}
                                                    >
                                                      {/* View Document Icon */}
                                                      <IconButton
                                                        size="small"
                                                        onClick={async (e) => {
                                                          e.stopPropagation();
                                                          try {
                                                            // Fetch guest documents
                                                            const docData = await getBookingGuestDocuments(booking.id || bookingId, currentGuest.id);
                                                            setSelectedGuestDocuments(buildGuestDocumentsPayload({
                                                              docData,
                                                              guest: currentGuest,
                                                              booking: { ...booking, payments: paymentHistory },
                                                              guestName,
                                                              guestPhone: currentGuest.phone || booking?.guest_phone || booking?.phone,
                                                              roomNumber: room.roomNumber,
                                                            }));
                                                            setDocumentDrawerOpen(true);
                                                          } catch (err) {// Still open drawer with available data
                                                            setSelectedGuestDocuments(buildGuestDocumentsPayload({
                                                              guest: currentGuest,
                                                              booking: { ...booking, payments: paymentHistory },
                                                              guestName,
                                                              guestPhone: currentGuest.phone || booking?.guest_phone || booking?.phone,
                                                              roomNumber: room.roomNumber,
                                                            }));
                                                            setDocumentDrawerOpen(true);
                                                          }
                                                        }}
                                                        sx={{
                                                          color: (currentGuest.has_id_front || currentGuest.has_id_back) 
                                                            ? CUSTOM_BORDER_COLOR 
                                                            : (isDark ? drawerTheme.subtleText : '#999'),
                                                          opacity: (currentGuest.has_id_front || currentGuest.has_id_back) ? 1 : 0.5,
                                                          '&:hover': {
                                                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                            opacity: 1,
                                                          },
                                                          width: 28,
                                                          height: 28,
                                                        }}
                                                      >
                                                        <DescriptionIcon sx={{ fontSize: 16 }} />
                                                      </IconButton>
                                                      
                                                      {/* Edit Guest Icon */}
                                                      <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleEditGuest(currentGuest);
                                                        }}
                                                        sx={{
                                                          color: CUSTOM_BORDER_COLOR,
                                                          '&:hover': {
                                                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                          },
                                                          '&.Mui-disabled': {
                                                            color: isDark ? '#666' : '#9e9e9e',
                                                          },
                                                          width: 28,
                                                          height: 28,
                                                        }}
                                                      >
                                                        <EditIcon sx={{ fontSize: 16 }} />
                                                      </IconButton>
                                                      
                                                      {/* Delete Guest Icon */}
                                                      {!currentGuest.isPrimary && (
                                                        <IconButton
                                                          size="small"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            const bookingItemId = findBookingItemIdForGuest(currentGuest);
                                                            if (!bookingItemId) {
                                                              setError('Unable to find booking item for this guest.');
                                                              return;
                                                            }
                                                            setGuestToDelete({
                                                              guest: currentGuest,
                                                              bookingItemId: bookingItemId,
                                                              guestName: guestName,
                                                            });
                                                            setDeleteGuestDialogOpen(true);
                                                          }}
                                                          sx={{
                                                            color: isDark ? '#f44336' : '#d32f2f',
                                                            '&:hover': {
                                                              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(211, 47, 47, 0.1)',
                                                            },
                                                            '&.Mui-disabled': {
                                                              color: isDark ? '#666' : '#9e9e9e',
                                                            },
                                                            width: 28,
                                                            height: 28,
                                                          }}
                                                        >
                                                          <DeleteIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                      )}
                      </Box>
                                                  </Box>
                                                </Paper>
                                                
                                                {/* Next Button */}
                                                {room.guests.length > 1 && (
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const nextIndex = safeIndex < room.guests.length - 1 ? safeIndex + 1 : 0;
                                                      setGuestSlideIndex(prev => ({ ...prev, [room.roomId]: nextIndex }));
                                                    }}
                                                    sx={{
                                                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                      color: CUSTOM_BORDER_COLOR,
                                                      border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                                                      width: 32,
                                                      height: 32,
                                                      '&:hover': {
                                                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.2)',
                                                        transform: 'scale(1.1)',
                                                      },
                                                      transition: 'all 0.2s ease',
                                                    }}
                                                  >
                                                    <ChevronRightIcon sx={{ fontSize: 20 }} />
                                                  </IconButton>
                                                )}
                                              </Box>
                                            );
                                          })()}
                                        </Box>
                                      ) : null}
                                      
                                      {/* Add Guest Button — capped by room adult count */}
                                      <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                          if (atGuestCapacity) return;
                                          handleAddGuest(room.items);
                                        }}
                                        disabled={atGuestCapacity || isRoomCheckedOut}
                                        title={
                                          atGuestCapacity
                                            ? `Maximum ${maxGuestsForRoom} guest${maxGuestsForRoom === 1 ? '' : 's'} for this room type`
                                            : undefined
                                        }
                                        sx={{
                                          mt: room.guests.length > 0 ? 1 : 0,
                                          color: CUSTOM_BORDER_COLOR,
                                          borderColor: CUSTOM_BORDER_COLOR,
                                          fontSize: '0.75rem',
                                          py: 0.5,
                                          px: 1.5,
                                          minHeight: 36,
                                          height: 36,
                                          borderRadius: '10px',
                                          alignSelf: 'stretch',
                                          width: '100%',
                                          justifyContent: 'center',
                                          '&:hover': {
                                            borderColor: CUSTOM_BORDER_COLOR,
                                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                          },
                                          '&.Mui-disabled': {
                                            color: isDark ? '#666' : '#9e9e9e',
                                            borderColor: isDark ? '#444' : '#d0d0d0',
                                          },
                                        }}
                                        variant="outlined"
                                        size="small"
                                      >
                                        {room.guests.length > 0 ? 'Add Another Guest' : 'Add Guest'}
                                      </Button>
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                );
              })()}


              {/* Payment Information - Hotel / Restaurant / Remaining */}
              <BookingDrawerPaymentCard
                booking={booking}
                payments={paymentHistory}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accentColor={CUSTOM_BORDER_COLOR}
                onAddPayment={async () => {
                  await fetchPaymentHistory();
                  setPaymentDrawerOpen(true);
                }}
                onRestaurantPaymentsChanged={async () => {
                  markBookingModified();
                  onStatusUpdate?.(getSafeBookingId());
                  void fetchBookingDetails();
                }}
              />
            </>
          )}
        </Box>

        {/* Footer - Fixed with Cancel and Modify Booking */}
          <Box
            sx={{
            position: 'relative',
            flexShrink: 0,
            p: 1.5,
              borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            zIndex: 10,
            boxShadow: isDark ? '0 -2px 8px rgba(0,0,0,0.3)' : '0 -2px 8px rgba(0,0,0,0.1)',
            }}
          >
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleDrawerClose}
              disabled={updating}
              sx={{
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  color: isDark ? drawerTheme.text : '#666',
                '&:hover': {
                    borderColor: CUSTOM_BORDER_COLOR,
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                    color: CUSTOM_BORDER_COLOR,
                  },
                  '&:disabled': {
                    borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.subtleText : '#999',
                },
              }}
            >
              Cancel
            </Button>
            </Grid>
            <Grid item xs={6}>
            {(() => {
              const modifyDisabled = updating || !booking || !canEditBooking;
              const modifyButton = (
            <Button
              variant="contained"
              fullWidth
                onClick={navigateToEditBooking}
                disabled={modifyDisabled}
              sx={{
                  backgroundColor: CUSTOM_BORDER_COLOR,
                  color: '#000',
                  fontWeight: 600,
                '&:hover': {
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    opacity: 0.9,
                    boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
                '&:disabled': {
                  backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.subtleText : '#666',
                },
              }}
            >
                {updating
                  ? <CircularProgress size={20} />
                  : 'Modify Booking'}
            </Button>
              );
              return !canEditBooking ? (
                <Tooltip title={getPermissionDeniedMessage(PERMISSIONS.BOOKING_MODIFY, 'Modify Booking')} arrow>
                  <span style={{ width: '100%', display: 'block' }}>{modifyButton}</span>
                </Tooltip>
              ) : modifyButton;
            })()}
            </Grid>
          </Grid>
          </Box>
      </Box>

      <CheckInDrawer
        bookingId={safeBookingIdForCheckIn}
        open={checkInDrawerOpen && Boolean(safeBookingIdForCheckIn)}
        onClose={handleCheckInDrawerClose}
        onCheckInSuccess={handleCheckInSuccessFromDetails}
        onModifyBooking={canEditBooking ? handleModifyBookingFromCheckIn : undefined}
        onBookingUpdated={() => {
          markBookingModified();
          if (safeBookingIdForCheckIn) {
            void fetchBookingDetails(safeBookingIdForCheckIn);
          }
        }}
      />

      {/* Confirmation Dialog - Inside drawer */}
      {confirmDialogOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
          onClick={() => setConfirmDialogOpen(false)}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
              borderRadius: 2,
              boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.2)',
              minWidth: 400,
              maxWidth: 500,
              p: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: isDark ? drawerTheme.text : '#333',
                fontWeight: 600,
                mb: 2,
              }}
            >
              Mark as No Show
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: isDark ? drawerTheme.subtleText : '#666',
                mb: 3,
              }}
            >
              Are you sure you want to mark this booking as "No Show"? This action cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setConfirmDialogOpen(false)}
                disabled={updating}
                sx={{
                  color: isDark ? drawerTheme.text : '#666',
                  '&:hover': {
                    backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkAsNoShow}
                variant="contained"
                disabled={updating}
                sx={{
                  backgroundColor: '#ff9800',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.subtleText : '#ccc',
                  },
                }}
              >
                {updating ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Error Popup - Inside drawer */}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
          onClick={() => setError(null)}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
              borderRadius: 2,
              boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.2)',
              minWidth: 400,
              maxWidth: 500,
              p: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: isDark ? drawerTheme.text : '#333',
                fontWeight: 600,
                mb: 2,
              }}
            >
              Error
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: isDark ? drawerTheme.subtleText : '#666',
                mb: 3,
              }}
            >
              {error}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setError(null)}
                variant="contained"
                sx={{
                  backgroundColor: '#f44336',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </Paper>
        </Box>
      )}


      {/* Guest Action Menu */}
      <Menu
        anchorEl={guestMenuAnchor}
        open={Boolean(guestMenuAnchor)}
        onClose={() => {
          setGuestMenuAnchor(null);
          setSelectedGuestForAction(null);
        }}
        {...withDrawerMenuPaperSx(menuProps, {
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
          minWidth: 200,
          mt: 1,
        })}
      >
        {selectedGuestForAction ? (
          <>
            <MenuItem
              onClick={() => {
                handleEditGuest(selectedGuestForAction);
                setGuestMenuAnchor(null);
              }}
              sx={{
                color: isDark ? drawerTheme.text : '#333',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                },
              }}
            >
              <ListItemIcon>
                <EditIcon sx={{ fontSize: 18, color: CUSTOM_BORDER_COLOR }} />
              </ListItemIcon>
              <ListItemText primary="Edit Guest" />
            </MenuItem>
            {!selectedGuestForAction.isPrimary && (
              <MenuItem
                onClick={() => {
                  // Find booking item for this guest using helper function
                  const bookingItemId = findBookingItemIdForGuest(selectedGuestForAction);
                  if (!bookingItemId) {
                    setError('Unable to find booking item for this guest.');
                    setGuestMenuAnchor(null);
                    return;
                  }
                  setGuestToDelete({
                    guest: selectedGuestForAction,
                    bookingItemId: bookingItemId,
                    guestName: [selectedGuestForAction.title, selectedGuestForAction.first_name, selectedGuestForAction.middle_name, selectedGuestForAction.last_name].filter(Boolean).join(' '),
                  });
                  setDeleteGuestDialogOpen(true);
                  setGuestMenuAnchor(null);
                  setSelectedGuestForAction(null);
                }}
                sx={{
                  color: isDark ? '#f44336' : '#d32f2f',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(211, 47, 47, 0.1)',
                  },
                }}
              >
                <ListItemIcon>
                  <DeleteIcon sx={{ fontSize: 18, color: isDark ? '#f44336' : '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText primary="Delete Guest" />
              </MenuItem>
            )}
          </>
        ) : (
          <MenuItem
            onClick={handleAddGuest}
            sx={{
              color: isDark ? drawerTheme.text : '#333',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
              },
            }}
          >
            <ListItemIcon>
              <PersonAddIcon sx={{ fontSize: 18, color: CUSTOM_BORDER_COLOR }} />
            </ListItemIcon>
            <ListItemText primary="Add Guest" />
          </MenuItem>
        )}
      </Menu>

      {/* Delete Guest Confirmation Dialog - Inside Drawer */}
      {deleteGuestDialogOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
              borderRadius: 3,
              minWidth: 400,
              maxWidth: 500,
              mx: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
              }}
            >
              <DeleteIcon sx={{ color: isDark ? '#f44336' : '#d32f2f', fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                Remove Guest
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ color: isDark ? drawerTheme.text : '#333', mb: 2 }}>
                Are you sure you want to remove <strong>{guestToDelete?.guestName || 'this guest'}</strong> from this booking?
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                This action cannot be undone. The guest will be removed from all booking items.
              </Typography>
            </Box>
            <Box sx={{ p: 2, borderTop: `1px solid ${isDark ? '#444' : '#e0e0e0'}`, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setDeleteGuestDialogOpen(false);
                  setGuestToDelete(null);
                }}
                variant="outlined"
                disabled={updating}
                sx={{
                  borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  color: isDark ? drawerTheme.text : '#666',
                  '&:hover': {
                    borderColor: isDark ? '#444' : '#ccc',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteGuest}
                variant="contained"
                disabled={updating}
                sx={{
                  backgroundColor: isDark ? '#f44336' : '#d32f2f',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: isDark ? '#d32f2f' : '#b71c1c',
                  },
                }}
              >
                {updating ? 'Removing...' : 'Remove'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {canViewBookingAudit && (
        <BookingAuditDrawer
          open={auditDrawerOpen}
          onClose={() => setAuditDrawerOpen(false)}
          bookingId={getSafeBookingId()}
          bookingCode={booking?.code}
          isDark={isDark}
        />
      )}

      <GuestDocumentsDrawer
        open={documentDrawerOpen}
        onClose={() => setDocumentDrawerOpen(false)}
        documents={selectedGuestDocuments}
        isDark={isDark}
      />

      {/* Guest Form Drawer - Exact copy from CheckInDrawer */}
      <Drawer
        anchor="right"
        open={guestFormDrawerOpen}
        onClose={() => {
          setGuestFormDrawerOpen(false);
          setEditingGuestItemId(null);
          setEditingGuestId(null);
          setStayGuestReplaceId(null);
          setIsStayGuestEdit(false);
          setIsPhoneLocked(false);
          setGuestFormError(null);
          setValidationErrors({});
          setTouchedFields({});
        }}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: { xs: '100%', sm: 600 },
          }),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                Guest Information
              </Typography>
              {booking && (
                <>
                  <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', mx: 0.5 }}>
                    -
                  </Typography>
                  <BookingDrawerCodeChip
                    booking={booking}
                    bookingId={bookingId}
                    label={booking.code}
                    onNavigate={handleDrawerClose}
                    sx={getGuestDrawerMetaChipSx(isDark)}
                  />
                </>
              )}
            </Box>
            {canManageAiCheckin && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AiGradientButton
                  size="small"
                  onClick={() => setAiAddGuestOpen(true)}
                  sx={{
                    minHeight: 30,
                    px: 1.15,
                    borderRadius: 999,
                    fontSize: '0.78rem',
                    fontWeight: 750,
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': {
                      mr: 0.5,
                      '& svg': { fontSize: 16 },
                    },
                  }}
                >
                  AI Add Guest
                </AiGradientButton>
              </Box>
            )}
          </Box>

          {/* Content - Scrollable */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 1.5,
              ...getGuestDrawerContentSx(isDark),
            }}
          >
            <Grid container spacing={2}>
              {/* First Row: Mobile Number (with country code and autocomplete), Title, Name */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  autoSelect={false}
                  autoHighlight={false}
                  disabled={false}
                  slotProps={autocompleteSlotProps}
                  options={guestSearchOptions}
                  filterOptions={(opts) => opts}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.phone || '';
                  }}
                  value={null}
                  inputValue={individualGuestFormData.phone || ''}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'selectOption') {
                      const selected = resolveGuestSearchOption(newInputValue);
                      if (selected) {
                        applySelectedGuestToForm(selected);
                        return;
                      }
                    }
                    if (reason === 'input' || reason === 'clear') {
                      handlePhoneChange(newInputValue);
                    }
                  }}
                  onChange={(event, newValue) => {
                    const selected = resolveGuestSearchOption(newValue);
                    if (selected) {
                      applySelectedGuestToForm(selected);
                      return;
                    }
                    if (!isStayGuestEdit && !newValue) {
                      setIsPhoneLocked(false);
                      setEditingGuestId(null);
                    }
                  }}
                  loading={searching}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="mobile"
                      label="Mobile Number"
                      required
                      error={!!validationErrors.phone && touchedFields.phone}
                      helperText={
                        (touchedFields.phone && validationErrors.phone)
                          || `Enter ${formatPhoneLengthLabel(individualGuestFormData.phone_country_code || '+91')}; if a saved guest appears, select them to load details`
                      }
                      onBlur={() => handleFieldBlur('phone')}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: getPhoneMaxLength(individualGuestFormData.phone_country_code || '+91'),
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                      }}
                      onInput={(e) => {
                        // Digits only, capped to country max length.
                        e.target.value = normalizePhoneInputForCountry(
                          e.target.value,
                          individualGuestFormData.phone_country_code || '+91',
                        );
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Box
                                ref={phoneCountryCodeAnchorRef}
                                onClick={() => {
                                  setPhoneCountryCodeMenuOpen(true);
                                }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  '&:hover': {
                                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                  },
                                }}
                              >
                                <Typography component="span" sx={{ fontSize: '1.2rem' }}>
                                  {selectedPhoneCountryCode?.flag || '🇮🇳'}
                                </Typography>
                                <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                  {selectedPhoneCountryCode?.dialCode || '+91'}
                                </Typography>
                                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                  ▼
                                </Typography>
                              </Box>
                            </InputAdornment>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                      '& fieldset': {
                        borderColor: (validationErrors.phone && touchedFields.phone) ? theme.palette.error.main : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                      },
                      '&:hover fieldset': {
                        borderColor: (validationErrors.phone && touchedFields.phone) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: (validationErrors.phone && touchedFields.phone) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: 'transparent !important',
                          color: isDark ? drawerTheme.text : '#333',
                        },
                        '& .MuiInputLabel-root': {
                          color: isDark ? drawerTheme.subtleText : '#666',
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? drawerTheme.text : '#333' }}>
                        {option.name}
                      </Typography>
                      {option.phone && (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                          {option.phone}
                        </Typography>
                      )}
                      {option.email && (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
                <Menu
                  anchorEl={phoneCountryCodeAnchorRef.current}
                  open={phoneCountryCodeMenuOpen}
                  onClose={() => {
                    setPhoneCountryCodeMenuOpen(false);
                    setPhoneCountryCodeSearch('');
                  }}
                  {...withDrawerMenuPaperSx(menuProps, {
                    maxHeight: 400,
                    width: 300,
                  })}
                >
                  <Box sx={{ p: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search country..."
                      value={phoneCountryCodeSearch}
                      onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  {countryCodes
                    .filter((country) => {
                      if (!phoneCountryCodeSearch) return true;
                      const search = phoneCountryCodeSearch.toLowerCase();
                      return (
                        country.name.toLowerCase().includes(search) ||
                        country.dialCode.includes(search) ||
                        country.code.toLowerCase().includes(search)
                      );
                    })
                    .map((country) => (
                      <MenuItem
                        key={country.code}
                        onClick={() => {
                          const dialCode = country.dialCode;
                          const trimmedPhone = normalizePhoneInputForCountry(
                            individualGuestFormData.phone,
                            dialCode,
                          );
                          setIndividualGuestFormData({
                            ...individualGuestFormData,
                            phone_country_code: dialCode,
                            phone: trimmedPhone,
                          });
                          if (touchedFields.phone) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              phone: validatePhone(trimmedPhone, dialCode),
                            }));
                          }
                          setPhoneCountryCodeMenuOpen(false);
                          setPhoneCountryCodeSearch('');
                        }}
                        selected={selectedPhoneCountryCode?.dialCode === country.dialCode}
                      >
                        <ListItemIcon>
                          <Typography sx={{ fontSize: '1.2rem' }}>{country.flag}</Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={country.dialCode}
                          secondary={country.name}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </MenuItem>
                    ))}
                </Menu>
              </Grid>
              {/* Name with Title */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={individualGuestFormData.name || ''}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onBlur={() => handleFieldBlur('name')}
                  required
                  error={!!validationErrors.first_name && (touchedFields.name || touchedFields.first_name)}
                  helperText={(touchedFields.name || touchedFields.first_name) ? validationErrors.first_name : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          ref={titleMenuAnchorRef}
                          onClick={() => setTitleMenuOpen(true)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                            },
                          }}
                        >
                          <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem', minWidth: 40 }}>
                            {individualGuestFormData.title || 'Title'}
                          </Typography>
                          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            ▼
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      '& fieldset': {
                        borderColor: (validationErrors.first_name && (touchedFields.name || touchedFields.first_name)) ? theme.palette.error.main : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                      },
                      '&:hover fieldset': {
                        borderColor: (validationErrors.first_name && (touchedFields.name || touchedFields.first_name)) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: (validationErrors.first_name && (touchedFields.name || touchedFields.first_name)) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                    },
                    '& .MuiInputBase-input': {
                      backgroundColor: 'transparent !important',
                      color: isDark ? drawerTheme.text : '#333',
                    },
                    '& .MuiInputLabel-root': {
                      color: isDark ? drawerTheme.subtleText : '#666',
                    },
                  }}
                />
                <Menu
                  anchorEl={titleMenuAnchorRef.current}
                  open={titleMenuOpen}
                  onClose={() => setTitleMenuOpen(false)}
                  {...withDrawerMenuPaperSx(menuProps, {
                    maxHeight: 300,
                    minWidth: 150,
                  })}
                >
                  <MenuItem
                    onClick={() => {
                      setIndividualGuestFormData({ ...individualGuestFormData, title: '' });
                      setTitleMenuOpen(false);
                    }}
                  >
                    <em>None</em>
                  </MenuItem>
                  {titleOptions.map((title) => (
                    <MenuItem
                      key={title.value}
                      onClick={() => {
                        setIndividualGuestFormData({ ...individualGuestFormData, title: title.value });
                        setTitleMenuOpen(false);
                      }}
                      selected={individualGuestFormData.title === title.value}
                    >
                      {title.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Grid>

              {/* Date of Birth + Gender (required — DOB identifies adult vs child) */}
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <HotelDatePicker
                    label="Date of Birth"
                    value={individualGuestFormData.dob ? moment(individualGuestFormData.dob) : null}
                    onChange={(date) => {
                      setTouchedFields((prev) => ({ ...prev, dob: true }));
                      if (date && date.isValid()) {
                        if (date.isAfter(moment(), 'day')) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            dob: 'Date of birth cannot be in the future',
                          }));
                          return;
                        }
                        setIndividualGuestFormData((prev) => ({
                          ...prev,
                          dob: date.format('YYYY-MM-DD'),
                        }));
                        setValidationErrors((prev) => {
                          const next = { ...prev };
                          delete next.dob;
                          return next;
                        });
                      } else {
                        setIndividualGuestFormData((prev) => ({ ...prev, dob: '' }));
                        setValidationErrors((prev) => ({
                          ...prev,
                          dob: 'Date of birth is required.',
                        }));
                      }
                    }}
                    maxDate={moment()}
                    slotProps={{
                      ...datePickerSlotProps,
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!validationErrors.dob && touchedFields.dob,
                        helperText: (touchedFields.dob && validationErrors.dob)
                          || (isGuestChild(individualGuestFormData.dob) ? 'Under 18 — counted as child' : ''),
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'transparent',
                            '& fieldset': {
                              borderColor: (validationErrors.dob && touchedFields.dob)
                                ? theme.palette.error.main
                                : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                            },
                            '&:hover fieldset': {
                              borderColor: (validationErrors.dob && touchedFields.dob)
                                ? theme.palette.error.main
                                : CUSTOM_BORDER_COLOR,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: (validationErrors.dob && touchedFields.dob)
                                ? theme.palette.error.main
                                : CUSTOM_BORDER_COLOR,
                            },
                          },
                          '& .MuiInputBase-input': {
                            backgroundColor: 'transparent !important',
                            color: isDark ? drawerTheme.text : '#333',
                          },
                          '& .MuiInputLabel-root': {
                            color: isDark ? drawerTheme.subtleText : '#666',
                          },
                          '& .MuiFormHelperText-root': {
                            color: isGuestChild(individualGuestFormData.dob) && !validationErrors.dob
                              ? (isDark ? '#90caf9' : '#1565c0')
                              : undefined,
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  required
                  error={!!validationErrors.gender && touchedFields.gender}
                >
                  <InputLabel sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                    Gender
                  </InputLabel>
                  <Select
                    label="Gender"
                    value={individualGuestFormData.gender || ''}
                    onChange={(e) => {
                      const value = e.target.value || '';
                      setIndividualGuestFormData((prev) => ({ ...prev, gender: value }));
                      setTouchedFields((prev) => ({ ...prev, gender: true }));
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        if (value) delete next.gender;
                        else next.gender = 'Gender is required.';
                        return next;
                      });
                    }}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, gender: true }))}
                    MenuProps={selectMenuProps}
                    sx={{
                      backgroundColor: 'transparent',
                      color: isDark ? drawerTheme.text : '#333',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: (validationErrors.gender && touchedFields.gender)
                          ? theme.palette.error.main
                          : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: (validationErrors.gender && touchedFields.gender)
                          ? theme.palette.error.main
                          : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: (validationErrors.gender && touchedFields.gender)
                          ? theme.palette.error.main
                          : CUSTOM_BORDER_COLOR,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Gender</em>
                    </MenuItem>
                    {GENDER_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touchedFields.gender && validationErrors.gender && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {validationErrors.gender}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Second Row: Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Guest Address"
                  value={individualGuestFormData.address || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setIndividualGuestFormData(prev => ({ ...prev, address: value }));
                    // Validate city and country when address changes if they've been touched
                    const hasAddress = !!(value && value.trim());
                    if (touchedFields.city) {
                      const cityError = validateCity(individualGuestFormData.city, hasAddress);
                      setValidationErrors(prev => ({ ...prev, city: cityError }));
                    }
                    if (touchedFields.country) {
                      const countryError = validateCountry(individualGuestFormData.country, hasAddress);
                      setValidationErrors(prev => ({ ...prev, country: countryError }));
                    }
                  }}
                  onBlur={() => handleFieldBlur('address')}
                  multiline
                  rows={2}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      '& fieldset': {
                        borderColor: isDark ? '#444' : '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: CUSTOM_BORDER_COLOR,
                      },
                    },
                    '& .MuiInputBase-input': {
                      backgroundColor: 'transparent !important',
                      color: isDark ? drawerTheme.text : '#333',
                    },
                    '& .MuiInputLabel-root': {
                      color: isDark ? drawerTheme.subtleText : '#666',
                    },
                  }}
                />
              </Grid>

              {/* Fourth Row: Country, State, City */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={countries}
                  slotProps={autocompleteSlotProps}
                  getOptionLabel={(option) => option.name}
                  filterOptions={countryFilterOptions}
                  value={countries.find(c => c.name === individualGuestFormData.country || c.code === individualGuestFormData.country) || null}
                  onChange={(event, newValue) => {
                    const countryValue = newValue ? newValue.name : '';
                    setIndividualGuestFormData(prev => ({ 
                      ...prev, 
                      country: countryValue,
                      state: '', // Reset state when country changes
                      city: '', // Reset city when country changes
                    }));
                    setTouchedFields(prev => ({ ...prev, country: true }));
                    applyGuestCountryChange(countryValue);
                  }}
                  onBlur={() => handleFieldBlur('country')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      error={
                        (!!validationErrors.country && touchedFields.country)
                        || !!validationErrors.foreignGuest
                      }
                      helperText={
                        validationErrors.foreignGuest
                          || (touchedFields.country && validationErrors.country)
                          || (isForeignGuest
                            ? 'Foreign guest details required'
                            : (individualGuestFormData.country ? 'Auto-selected from hotel' : ''))
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: isDark ? '#444' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: 'transparent !important',
                          color: isDark ? drawerTheme.text : '#333',
                        },
                        '& .MuiInputLabel-root': {
                          color: isDark ? drawerTheme.subtleText : '#666',
                        },
                      }}
                    />
                  )}
                />
                {isForeignGuest ? (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      icon={<PublicIcon sx={{ fontSize: '16px !important' }} />}
                      label={
                        foreignGuestComplete
                          ? 'Foreign guest details saved'
                          : 'Add foreign guest details'
                      }
                      onClick={() => setForeignGuestDrawerOpen(true)}
                      color={foreignGuestComplete ? 'success' : 'warning'}
                      variant={foreignGuestComplete ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 700 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setForeignGuestDrawerOpen(true)}
                      aria-label="Edit foreign guest details"
                      sx={{ color: CUSTOM_BORDER_COLOR }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ) : null}
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={availableStates}
                  slotProps={autocompleteSlotProps}
                  getOptionLabel={(option) => option.name}
                  filterOptions={stateFilterOptions}
                  value={availableStates.find(s => s.name === individualGuestFormData.state) || null}
                  onChange={(event, newValue) => {
                    setIndividualGuestFormData({ 
                      ...individualGuestFormData, 
                      state: newValue ? newValue.name : '',
                      city: '', // Reset city when state changes
                    });
                  }}
                  disabled={!selectedCountryObj}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: (validationErrors.country && touchedFields.country) ? theme.palette.error.main : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                          },
                          '&:hover fieldset': {
                            borderColor: (validationErrors.country && touchedFields.country) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: (validationErrors.country && touchedFields.country) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: 'transparent !important',
                          color: isDark ? drawerTheme.text : '#333',
                        },
                        '& .MuiInputLabel-root': {
                          color: isDark ? drawerTheme.subtleText : '#666',
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={availableCities}
                  slotProps={autocompleteSlotProps}
                  getOptionLabel={(option) => option}
                  filterOptions={cityFilterOptions}
                  value={availableCities.find(c => c === individualGuestFormData.city) || null}
                  onChange={(event, newValue) => {
                    const cityValue = newValue || '';
                    setIndividualGuestFormData(prev => ({ ...prev, city: cityValue }));
                    // Validate city when it changes
                    const hasAddress = !!(individualGuestFormData.address && individualGuestFormData.address.trim());
                    if (touchedFields.city || hasAddress) {
                      const error = validateCity(cityValue, hasAddress);
                      setValidationErrors(prev => ({ ...prev, city: error }));
                      setTouchedFields(prev => ({ ...prev, city: true }));
                    }
                  }}
                  onBlur={() => handleFieldBlur('city')}
                  disabled={!selectedCountryObj || !individualGuestFormData.state}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      error={!!validationErrors.city && touchedFields.city}
                      helperText={touchedFields.city ? validationErrors.city : ''}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor: isDark ? '#444' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: 'transparent !important',
                          color: isDark ? drawerTheme.text : '#333',
                        },
                        '& .MuiInputLabel-root': {
                          color: isDark ? drawerTheme.subtleText : '#666',
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Email and Pincode Row */}
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={individualGuestFormData.email || ''}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => handleFieldBlur('email')}
                  error={!!validationErrors.email && touchedFields.email}
                  helperText={touchedFields.email ? validationErrors.email : ''}
                  inputProps={{
                    autoComplete: 'email',
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      '& fieldset': {
                        borderColor: (validationErrors.email && touchedFields.email) ? theme.palette.error.main : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                      },
                      '&:hover fieldset': {
                        borderColor: (validationErrors.email && touchedFields.email) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: (validationErrors.email && touchedFields.email) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                    },
                    '& .MuiInputBase-input': {
                      backgroundColor: 'transparent !important',
                      color: isDark ? drawerTheme.text : '#333',
                    },
                    '& .MuiInputLabel-root': {
                      color: isDark ? drawerTheme.subtleText : '#666',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={individualGuestFormData.zipcode || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setIndividualGuestFormData(prev => ({ ...prev, zipcode: value }));
                    if (touchedFields.zipcode) {
                      const error = validatePincode(value);
                      setValidationErrors(prev => ({ ...prev, zipcode: error }));
                    }
                  }}
                  onBlur={() => handleFieldBlur('zipcode')}
                  error={!!validationErrors.zipcode && touchedFields.zipcode}
                  helperText={touchedFields.zipcode ? validationErrors.zipcode : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      '& fieldset': {
                        borderColor: isDark ? '#444' : '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: CUSTOM_BORDER_COLOR,
                      },
                    },
                    '& .MuiInputBase-input': {
                      backgroundColor: 'transparent !important',
                      color: isDark ? drawerTheme.text : '#333',
                    },
                    '& .MuiInputLabel-root': {
                      color: isDark ? drawerTheme.subtleText : '#666',
                    },
                  }}
                />
              </Grid>

              {/* Do Not Rent */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mb: individualGuestFormData.do_not_rent ? 1 : 0,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={individualGuestFormData.do_not_rent}
                        onChange={(e) => setIndividualGuestFormData({ ...individualGuestFormData, do_not_rent: e.target.checked })}
                        color="primary"
                      />
                    }
                    label="Do not Rent"
                    sx={{ color: isDark ? drawerTheme.text : '#333', m: 0 }}
                  />
                  {individualGuestFormData.do_not_rent && (
                    <>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Severity</InputLabel>
                        <Select
                          value={individualGuestFormData.do_not_rent_severity}
                          label="Severity"
                          MenuProps={selectMenuProps}
                          onChange={(e) => setIndividualGuestFormData({ ...individualGuestFormData, do_not_rent_severity: e.target.value })}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: isDark ? '#444' : '#e0e0e0',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: CUSTOM_BORDER_COLOR,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: CUSTOM_BORDER_COLOR,
                            },
                          }}
                        >
                          {severityOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </Box>
                {individualGuestFormData.do_not_rent && (
                  <TextField
                    fullWidth
                    label="Reason for do not rent"
                    value={individualGuestFormData.do_not_rent_reason}
                    onChange={(e) => setIndividualGuestFormData({ ...individualGuestFormData, do_not_rent_reason: e.target.value })}
                    multiline
                    rows={2}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        '& fieldset': {
                          borderColor: (validationErrors.country && touchedFields.country) ? theme.palette.error.main : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                        },
                        '&:hover fieldset': {
                          borderColor: (validationErrors.country && touchedFields.country) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: (validationErrors.country && touchedFields.country) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                        },
                      },
                      '& .MuiInputBase-input': {
                        backgroundColor: 'transparent',
                        color: isDark ? drawerTheme.text : '#333',
                      },
                      '& .MuiInputLabel-root': {
                        color: isDark ? drawerTheme.subtleText : '#666',
                      },
                    }}
                  />
                )}
              </Grid>

              {/* Document Upload Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={isForeignGuest}>
                      <InputLabel>Select Document type</InputLabel>
                      <Select
                        value={individualGuestFormData.document_type}
                        label="Select Document type"
                        disabled={isForeignGuest}
                        MenuProps={selectMenuProps}
                        onChange={(e) => {
                          if (isForeignGuest) return;
                          const nextType = e.target.value;
                          setIndividualGuestFormData(prev => ({
                            ...prev,
                            document_type: nextType,
                            id_number: nextType === NO_DOCUMENT_VALUE ? '' : prev.id_number,
                            id_front: nextType === NO_DOCUMENT_VALUE ? '' : prev.id_front,
                            id_back: nextType === NO_DOCUMENT_VALUE ? '' : prev.id_back,
                          }));
                          if (nextType === NO_DOCUMENT_VALUE) {
                            setValidationErrors(prev => ({ ...prev, id_number: '' }));
                            return;
                          }
                          if (touchedFields.id_number || touchedFields.document_type) {
                            const error = validateDocumentIdNumber(nextType, individualGuestFormData.id_number);
                            setValidationErrors(prev => ({ ...prev, id_number: error }));
                          }
                        }}
                        onBlur={() => handleFieldBlur('document_type')}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor:
                              (validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type))
                                ? theme.palette.error.main
                                : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor:
                              (validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type))
                                ? theme.palette.error.main
                                : CUSTOM_BORDER_COLOR,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor:
                              (validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type))
                                ? theme.palette.error.main
                                : CUSTOM_BORDER_COLOR,
                          },
                        }}
                      >
                        {documentUploadOptions.map((doc) => (
                          <MenuItem key={doc.value} value={doc.value}>
                            {doc.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {isForeignGuest ? (
                        <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, color: 'text.secondary' }}>
                          Set from Foreign Guest Details
                        </Typography>
                      ) : null}
                    </FormControl>
                  </Grid>
                  {!isNoDocumentSelected && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={`${getDocumentTypeLabel()} Number`}
                        value={normalizedIdNumberValue}
                        disabled={isForeignGuest}
                      onChange={(e) => {
                        if (isForeignGuest) return;
                        const value = normalizeDocumentIdInput(
                          effectiveDocumentType,
                          e.target.value
                        );
                        setIndividualGuestFormData(prev => ({ ...prev, id_number: value }));
                        if (touchedFields.id_number) {
                          const error = validateDocumentIdNumber(effectiveDocumentType, value);
                          setValidationErrors(prev => ({ ...prev, id_number: error }));
                        }
                      }}
                      onBlur={() => handleFieldBlur('id_number')}
                      placeholder={getDocumentNumberPlaceholder()}
                      required
                      error={!!validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type)}
                      helperText={(touchedFields.id_number || touchedFields.document_type) ? validationErrors.id_number : ''}
                      inputProps={{
                        maxLength: isAadhaarDocumentSelected ? 14 : undefined,
                        inputMode: isAadhaarDocumentSelected ? 'numeric' : undefined,
                        pattern: isAadhaarDocumentSelected ? '[0-9\\-]*' : undefined,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                          '& fieldset': {
                            borderColor:
                              (validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type))
                                ? theme.palette.error.main
                                : (isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)'),
                          },
                          '&:hover fieldset': {
                            borderColor:
                              (validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type))
                                ? theme.palette.error.main
                                : CUSTOM_BORDER_COLOR,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor:
                              (validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type))
                                ? theme.palette.error.main
                                : CUSTOM_BORDER_COLOR,
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: 'transparent !important',
                          color: isDark ? drawerTheme.text : '#333',
                        },
                        '& .MuiInputLabel-root': {
                          color: isDark ? drawerTheme.subtleText : '#666',
                        },
                      }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', md: 'flex-end' },
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <FormControlLabel
                            label="Camera upload"
                            labelPlacement="start"
                            control={
                              <Switch
                                checked={individualGuestFormData.camera_upload}
                                onChange={(e) => setIndividualGuestFormData({ ...individualGuestFormData, camera_upload: e.target.checked })}
                                color="primary"
                              />
                            }
                            sx={{ color: isDark ? drawerTheme.text : '#333' }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* ID Front */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                  {getFrontDocumentLabel()}
                  {!isNoDocumentSelected &&
                  !individualGuestFormData.id_front &&
                  !individualGuestFormData.id_back
                    ? ' *'
                    : ''}
                </Typography>
                <Paper
                  sx={getGuestDrawerUploadPaperSx(isDark, {
                    ...(validationErrors.id_front && touchedFields.id_front
                      ? {
                          borderColor: '#d32f2f',
                          backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
                          '&:hover': {
                            borderColor: '#d32f2f',
                            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)',
                          },
                        }
                      : {}),
                  })}
                  onClick={() => handleUploadClick('front')}
                >
                  {individualGuestFormData.id_front ? (
                    <Box>
                      <img
                        src={individualGuestFormData.id_front}
                        alt="ID Front"
                        style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                      />
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const nextFront = '';
                          const nextBack = individualGuestFormData.id_back;
                          setIndividualGuestFormData((prev) => ({ ...prev, id_front: nextFront }));
                          syncDocumentImageErrors(nextFront, nextBack);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      {individualGuestFormData.camera_upload ? (
                        <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                      ) : (
                        <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                      )}
                      <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                        {individualGuestFormData.camera_upload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                      </Typography>
                    </Box>
                  )}
                  <input
                    ref={fileInputFrontRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileUpload(e, 'front')}
                  />
                </Paper>
                {validationErrors.id_front && touchedFields.id_front && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                    {validationErrors.id_front}
                  </Typography>
                )}
              </Grid>

              {/* ID Back */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                  {getBackDocumentLabel()}
                  {!isNoDocumentSelected &&
                  !individualGuestFormData.id_front &&
                  !individualGuestFormData.id_back
                    ? ' *'
                    : ''}
                </Typography>
                <Paper
                  sx={getGuestDrawerUploadPaperSx(isDark, {
                    ...(validationErrors.id_back && touchedFields.id_back
                      ? {
                          borderColor: '#d32f2f',
                          backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
                          '&:hover': {
                            borderColor: '#d32f2f',
                            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)',
                          },
                        }
                      : {}),
                  })}
                  onClick={() => handleUploadClick('back')}
                >
                  {individualGuestFormData.id_back ? (
                    <Box>
                      <img
                        src={individualGuestFormData.id_back}
                        alt="ID Back"
                        style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                      />
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const nextFront = individualGuestFormData.id_front;
                          const nextBack = '';
                          setIndividualGuestFormData((prev) => ({ ...prev, id_back: nextBack }));
                          syncDocumentImageErrors(nextFront, nextBack);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      {individualGuestFormData.camera_upload ? (
                        <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                      ) : (
                        <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                      )}
                      <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                        {individualGuestFormData.camera_upload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                      </Typography>
                    </Box>
                  )}
                  <input
                    ref={fileInputBackRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileUpload(e, 'back')}
                  />
                </Paper>
                {validationErrors.id_back && touchedFields.id_back && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                    {validationErrors.id_back}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              gap: 1.5,
              ...getBookingDrawerFooterSx(isDark),
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setGuestFormDrawerOpen(false);
                setEditingGuestItemId(null);
                setEditingGuestId(null);
                setStayGuestReplaceId(null);
                setIsStayGuestEdit(false);
                setIsPhoneLocked(false);
                setGuestFormError(null);
                setValidationErrors({});
                setTouchedFields({});
              }}
              disabled={savingGuest}
              sx={getBookingDrawerOutlinedButtonSx(isDark)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSaveIndividualGuest}
              disabled={savingGuest || !individualGuestFormData.name || !individualGuestFormData.name.trim()}
              sx={getBookingDrawerPrimaryButtonSx(isDark)}
            >
              {savingGuest ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </Box>
          {canManageAiCheckin && (
            <WalkInAiCheckinOverlay
              open={aiAddGuestOpen}
              onClose={() => setAiAddGuestOpen(false)}
              onSuccess={handleAiAddGuestSuccess}
              isDark={isDark}
              title="AI Add Guest"
            />
          )}
          <ForeignGuestDetailsDrawer
            open={foreignGuestDrawerOpen}
            onClose={() => setForeignGuestDrawerOpen(false)}
            hotelCountry={hotelCountry}
            guestCountry={individualGuestFormData.country}
            checkInDate={booking?.check_in_date || null}
            initialDetails={foreignGuestDetails}
            onSave={(nextDetails) => {
              setForeignGuestDetails(nextDetails);
              if (nextDetails.passportNumber) {
                setIndividualGuestFormData((prev) => ({
                  ...prev,
                  document_type: 'passport',
                  id_number: String(nextDetails.passportNumber).trim(),
                }));
              }
              setValidationErrors((prev) => ({ ...prev, foreignGuest: '' }));
              setForeignGuestDrawerOpen(false);
            }}
          />
          {guestFormError && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 35,
                bgcolor: isDark ? 'rgba(0,0,0,0.68)' : 'rgba(15,23,42,0.28)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
              onClick={() => setGuestFormError(null)}
            >
              <Paper
                onClick={(e) => e.stopPropagation()}
                sx={{
                  width: '100%',
                  maxWidth: 420,
                  p: 2.25,
                  borderRadius: 2.5,
                  backgroundColor: isDark ? drawerTheme.surface : '#fff',
                  border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#fecaca'}`,
                  boxShadow: isDark ? '0 18px 42px rgba(0,0,0,0.45)' : '0 18px 42px rgba(15,23,42,0.18)',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: isDark ? drawerTheme.text : '#991b1b' }}>
                  Could not save guest
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#374151', lineHeight: 1.55, mb: 2 }}>
                  {guestFormError}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setGuestFormError(null)}
                  sx={getBookingDrawerPrimaryButtonSx(isDark)}
                >
                  OK
                </Button>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Camera Modal */}
        {cameraOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                stopCamera();
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                {cameraType === 'front' ? 'Capture ID Front' : 'Capture ID Back'}
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'black',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, width: '100%' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capturePhoto}
                  size="large"
                  sx={{ minWidth: 150, py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                >
                  Capture Photo
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={stopCamera}
                  size="large"
                  sx={{
                    minWidth: 150,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Payment Drawer */}
      <Drawer
        anchor="right"
        open={paymentDrawerOpen}
        onClose={() => {
          setPaymentDrawerOpen(false);
          setPaymentAmount(0);
          setPaymentMethod('');
          setPaymentLinkSendOpen(false);
          setReferenceNumber('');
          setPaymentReceipt(null);
          setPaymentCameraUpload(false);
        }}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
          },
        }}
      >
        <Box ref={paymentDrawerRootRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
              }}
            >
              <PaymentDrawerHeaderTitle
                booking={booking}
                bookingId={bookingId}
                paymentHistory={paymentHistory}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accentColor={CUSTOM_BORDER_COLOR}
              />
              <IconButton onClick={() => {
                setPaymentDrawerOpen(false);
                setPaymentAmount(0);
                setPaymentMethod('');
                setPaymentLinkSendOpen(false);
                setReferenceNumber('');
                setPaymentReceipt(null);
                setPaymentCameraUpload(false);
              }} sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            {/* Progress Bar */}
            {submittingPayment && (
              <LinearProgress
                sx={{
                  height: 3,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: CUSTOM_BORDER_COLOR,
                  },
                }}
              />
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Payment Summary */}
            {booking && (
              <BookingPaymentSummaryCard
                summary={getPaymentSummaryData(booking, paymentHistory)}
                booking={booking}
                bookingId={getSafeBookingId()}
                isDark={isDark}
                drawerTheme={drawerTheme}
                overlayMountRef={paymentDrawerRootRef}
                onExtraChargesSaved={(result) => {
                  if (result?.booking) {
                    setBooking(result.booking);
                  } else {
                    fetchBookingDetails();
                  }
                }}
              />
            )}

            {/* Show success message when payment is clear (hotel + restaurant dues) */}
            {booking && isBookingPaymentClear(getEnrichedPaymentSummaryData(booking, paymentHistory)) ? (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2,
                    p: 1.5,
                    backgroundColor: isDark 
                      ? 'rgba(76, 175, 80, 0.1)' 
                      : 'rgba(76, 175, 80, 0.05)',
                    border: `2px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.25)'}`,
                    borderRadius: '12px',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)`,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                      }}
                    >
                      <CheckCircleIcon sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        component="div"
                        variant="body2" 
                        sx={{ 
                          fontWeight: 700, 
                          color: isDark ? '#81c784' : '#2e7d32',
                          fontSize: '0.9rem',
                          mb: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        Payment Clear
                        <Chip
                          label="Complete"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            backgroundColor: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)',
                            color: isDark ? '#81c784' : '#2e7d32',
                            border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)'}`,
                          }}
                        />
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isDark ? drawerTheme.subtleText : '#666',
                          fontSize: '0.8rem',
                          lineHeight: 1.5,
                          display: 'block',
                        }}
                      >
                        All payments have been collected for this booking.
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
            ) : null}

            {/* Payment History */}
            {(loadingPayments) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, mb: 2 }}>
                <CircularProgress size={24} sx={{ color: CUSTOM_BORDER_COLOR }} />
              </Box>
            ) : (
              <BookingFinancialHistoryPanel
                payments={Array.isArray(paymentHistory) ? paymentHistory : []}
                currency={booking?.currency || 'INR'}
                hotelTimezone={booking?.hotel_timezone}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accent={CUSTOM_BORDER_COLOR}
                defaultExpanded={false}
              />
            )}

            {/* Collect Payment Form */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                borderRadius: '12px',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600, mb: 1.5 }}>
                Collect Payment
              </Typography>

              <Grid container spacing={1.5}>
                {/* Payment Amount */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentAmount != null && paymentAmount !== '' ? round2(Number(paymentAmount)) : ''}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setPaymentAmount(Number.isFinite(value) && value >= 0 ? round2(value) : 0);
                    }}
                    inputProps={{
                      step: "0.01",
                      min: "0",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {bookingCurrencySymbol}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label="Payment Date & Time"
                      disableFuture
                      value={paymentDate}
                      onChange={setPaymentDate}
                      slotProps={{
                        ...datePickerSlotProps,
                        textField: {
                          fullWidth: true,
                          size: 'medium',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                              '& fieldset': {
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Payment Method - Chips */}
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', fontWeight: 600, mb: 1, display: 'block' }}>
                    Payment Method *
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {paymentMethods.map((method) => (
                      <Chip
                        key={method}
                        label={method}
                        onClick={() => {
                          setPaymentMethod(method);
                          setPaymentLinkSendOpen(method === PAYMENT_LINK_METHOD && canSendPaymentLink);
                        }}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: paymentMethod === method
                            ? CUSTOM_BORDER_COLOR
                            : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                          color: paymentMethod === method
                            ? '#000'
                            : CUSTOM_BORDER_COLOR,
                          border: `1px solid ${paymentMethod === method
                            ? CUSTOM_BORDER_COLOR
                            : (isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)')}`,
                          fontWeight: paymentMethod === method ? 700 : 600,
                          fontSize: '0.75rem',
                          height: 32,
                          '&:hover': {
                            backgroundColor: paymentMethod === method
                              ? CUSTOM_BORDER_COLOR
                              : (isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'),
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>

                {/* Reference Number */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reference Number (Optional)"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Payment Receipt Upload */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                        Payment Receipt (Optional)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                        <FormControlLabel
                          label="Camera upload"
                          labelPlacement="start"
                          control={
                            <Switch
                              checked={paymentCameraUpload}
                              onChange={(e) => setPaymentCameraUpload(e.target.checked)}
                              color="primary"
                            />
                          }
                          sx={{ color: isDark ? drawerTheme.text : '#333' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Paper
                    sx={{
                      p: 2,
                      border: `2px dashed ${CUSTOM_BORDER_COLOR}`,
                      borderRadius: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      minHeight: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                    onClick={handlePaymentReceiptClick}
                  >
                    {paymentReceipt ? (
                      <Box>
                        <img
                          src={paymentReceipt}
                          alt="Payment Receipt"
                          style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                        />
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPaymentReceipt(null);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        {paymentCameraUpload ? (
                          <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                        ) : (
                          <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                        )}
                        <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                          {paymentCameraUpload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                        </Typography>
                      </Box>
                    )}
                    <input
                      ref={paymentReceiptFileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handlePaymentReceiptUpload}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setPaymentDrawerOpen(false);
                    setPaymentAmount(0);
                    setPaymentMethod('');
                    setPaymentLinkSendOpen(false);
                    setReferenceNumber('');
                    setPaymentReceipt(null);
                    setPaymentCameraUpload(false);
                  }}
                  disabled={submittingPayment}
                  sx={{
                    borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.text : '#666',
                    '&:hover': {
                      borderColor: CUSTOM_BORDER_COLOR,
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: CUSTOM_BORDER_COLOR,
                    },
                    '&:disabled': {
                      borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                      color: isDark ? drawerTheme.subtleText : '#999',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmitPayment}
                  disabled={submittingPayment || !paymentAmount || !paymentMethod || paymentMethod === 'PAYMENT LINK'}
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                      boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    },
                    '&:disabled': {
                      backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                      color: isDark ? drawerTheme.subtleText : '#666',
                    },
                  }}
                >
                  {submittingPayment ? <CircularProgress size={20} /> : 'Collect Payment'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {canSendPaymentLink ? (
          <PaymentLinkDrawerOverlay
            open={paymentLinkSendOpen}
            onClose={() => setPaymentLinkSendOpen(false)}
            bookingId={getSafeBookingId()}
            booking={booking}
            amount={paymentAmount}
            currency={booking?.currency || 'INR'}
            isDark={isDark}
            accent={CUSTOM_BORDER_COLOR}
            canSendEmail={canSendPaymentLinkEmail}
            canSendWhatsApp={canSendPaymentLinkWhatsApp}
            onSentSuccess={(msg) => setPaymentLinkToast({ open: true, message: msg })}
            onAfterSend={async () => {
              await fetchPaymentHistory();
              await fetchBookingDetails();
              markBookingModified();
            }}
          />
          ) : null}
        </Box>

        {/* Payment Receipt Camera Modal */}
        {paymentCameraOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              zIndex: 1500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                stopPaymentCamera();
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                Capture Payment Receipt
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'black',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <video
                  ref={paymentReceiptVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <canvas ref={paymentReceiptCanvasRef} style={{ display: 'none' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, width: '100%' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capturePaymentReceipt}
                  size="large"
                  sx={{ minWidth: 150, py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                >
                  Capture Photo
                </Button>
                <Button
                  variant="outlined"
                  onClick={stopPaymentCamera}
                  size="large"
                  sx={{
                    minWidth: 150,
                    py: 1.5,
                    fontSize: '1rem',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </Drawer>

    <Snackbar
      open={cameraPermissionSnackbar.open}
      autoHideDuration={9000}
      onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
        severity="warning"
        variant="filled"
        sx={{ width: '100%', alignItems: 'center' }}
      >
        {cameraPermissionSnackbar.message}
      </Alert>
    </Snackbar>
    <Snackbar
      open={paymentLinkToast.open}
      autoHideDuration={4000}
      onClose={() => setPaymentLinkToast({ open: false, message: '' })}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setPaymentLinkToast({ open: false, message: '' })}
        severity="success"
        variant="filled"
        sx={{
          width: '100%',
          alignItems: 'center',
          backgroundColor: CUSTOM_BORDER_COLOR,
          color: '#000',
          fontWeight: 600,
          '& .MuiAlert-icon': { color: '#000' },
        }}
      >
        {paymentLinkToast.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default BookingDetailsDrawer;

